import { z } from 'zod';
import { introspectionTypes, isNonNullType, isListType, isScalarType, isEnumType, isInputObjectType, buildSchema, isObjectType, isUnionType, isInterfaceType, GraphQLList, parse, Kind, isInputType, typeFromAST } from 'graphql';
import { isQueryVariable } from '@hypertune/sdk/src/shared/types';
import { graphqlTypeNameKey, isPartialObjectKey, isQueryVariableKey } from '@hypertune/sdk/src/shared/constants';
import throws from '@hypertune/sdk/src/shared/helpers/throws';
import nullThrows2 from '@hypertune/sdk/src/shared/helpers/nullThrows';
import { getInlineFragment, stableStringify, defaultBranchName, mapExpression, fieldPathSeparator, uniqueId as uniqueId$1, mapExpressionWithResult, getBooleanExpression, nullThrows, defaultArmKey, hash, mergeLogs, getStringExpression, getFloatExpression, getIntExpression, isPartialObjectKey as isPartialObjectKey$1, getNoOpExpression, BooleanExpressionType, IntExpressionType, FloatExpressionType, StringExpressionType, RegexExpressionType, EnumExpressionType } from '@hypertune/sdk/src/shared';
import uniqueId from '@hypertune/sdk/src/shared/helpers/uniqueId';
import { UAParser } from 'ua-parser-js';
import getSplitsAndCommitConfigForExpression from '@hypertune/sdk/src/shared/helpers/getSplitsAndCommitConfigForExpression';
import { toStartCaseWords } from '@hypertune/sdk/src/shared/helpers/toStartCase';
import { uniqueId as uniqueId$2 } from '@hypertune/sdk/src/shared/helpers';
export { default as calculateFrequentistMetrics } from '@hypertune/sdk/src/shared/helpers/calculateFrequentistMetrics';
export { default as calculateBayesianProbability } from '@hypertune/sdk/src/shared/helpers/calculateBayesianProbability';
import { jsx } from 'react/jsx-runtime';

// src/types.ts
var analyticsViewConfigFunnelType = "funnel";
var analyticsViewConfigTableType = "table";
var analyticsViewConfigTypes = [
  analyticsViewConfigFunnelType,
  analyticsViewConfigTableType
];
var funnelEventStepType = "FunnelEventStep";
var funnelExposureStepType = "FunnelExposureStep";
var funnelStepTypes = [
  funnelEventStepType,
  funnelExposureStepType
];
var analyticsAggregationTypes = [
  "sum",
  "average",
  "minimum",
  "maximum"
];
var sdkVersions = ["1.0.0", "2.0.0", "2.7.0"];
var projectTokenScopes = z.enum([
  "all",
  "query",
  "logs",
  "codegen",
  "flagDefinitions"
]);
var projectTokenScopesArray = z.array(projectTokenScopes);
var projectTokenMapSchema = z.record(
  z.object({ scopes: z.array(projectTokenScopes) })
);
var updateCommitSyncBodySchema = z.object({
  commitId: z.number(),
  type: z.enum(["edge", "vercel", "webhook"]),
  targetId: z.string(),
  status: z.enum(["success", "error"]),
  message: z.nullable(z.string())
});
var edgeCommitMetadataSchema = z.object({
  allowToolbarGeneration: z.boolean(),
  blockAllRequests: z.optional(z.boolean())
});
var updateActiveCommitRequestBody = z.object({
  tokenMap: projectTokenMapSchema,
  branch: z.string(),
  businessId: z.number(),
  projectId: z.number(),
  commitId: z.number(),
  hash: z.string(),
  schemaCode: z.string(),
  expressionJson: z.string(),
  splitsJson: z.string(),
  eventTypesJson: z.string(),
  featuresJson: z.string(),
  configJson: z.string(),
  metadata: z.optional(z.nullable(edgeCommitMetadataSchema))
});

// src/schema/fieldArgumentsObjectTypeName.ts
var argsSuffix = "args";
var argsObjectSuffix = `_${argsSuffix}`;
function getFieldArgumentsObjectTypeName(parts) {
  return `${parts.parentObjectTypeName}_${parts.fieldName}_${argsSuffix}`;
}
function getFieldArgumentsObjectTypeNameParts(fieldArgumentsObjectTypeName) {
  const parts = fieldArgumentsObjectTypeName.split("_");
  if (parts.length < 3 || parts[parts.length - 1] !== argsSuffix) {
    return null;
  }
  return {
    parentObjectTypeName: parts[0],
    fieldName: parts.slice(1, parts.length - 1).join("_")
  };
}
function isFieldArgumentsObjectVariableExpression(expression) {
  return !!expression && expression.type === "VariableExpression" && expression.valueType.type === "ObjectValueType" && !!getFieldArgumentsObjectTypeNameParts(expression.valueType.objectTypeName);
}

// src/constants.ts
var anonymousIdCookieName = "hypertuneAnonymousId";
var sessionIdParamName = "hypertuneSessionId";
var sessionIdHeaderName = "X-Hypertune-Session-Id";
var appVersionHeaderName = "X-Version";
var rightDoubleArrowSymbol = "\u21D2";
var voidGraphqlScalarTypeName = "Void";
var queryObjectTypeName = "Query";
var sourceObjectTypeName = "Source";
var rootFieldName = "root";
var rootFieldArgsTypeName = "RootArgs";
var rootObjectTypeName = "Root";
var queryRootArgsTypeName = "Query_root_args";
var contextInputs = ["user", "team", "org", "organization", "wallet"];
var contextInputsSet = new Set(contextInputs);
var contextInputsWeights = Object.fromEntries(
  contextInputs.map((value, index) => [value, index])
);
var contextInputFields = [
  "id",
  "uuid",
  "uid",
  "guid",
  "slug",
  "email",
  "address"
];
var contextInputFieldsSet = new Set(contextInputFields);
var contextInputFieldsWeights = Object.fromEntries(
  contextInputFields.map((value, index) => [value, index])
);
var kilSwitchExpressionNote = "Kill switch";
var graphqlScalarTypeNames = [
  voidGraphqlScalarTypeName,
  "Boolean",
  "Int",
  "Float",
  "String"
];
var allGraphqlScalarTypeNames = [...graphqlScalarTypeNames];
var reservedTypeNames = [rootFieldArgsTypeName, sourceObjectTypeName];
var queryRootArgsObjectTypeName = getFieldArgumentsObjectTypeName({
  parentObjectTypeName: queryObjectTypeName,
  fieldName: rootFieldName
});
var defaultArmLabel = "Default";
var variableExpressionOptionsGroupLabel = "Variables";
var otherExpressionOptionsGroupLabel = "Logic";
var unimplementedExpressionErrorMessage = "Expression is not implemented.";
var cookieConsentCookieName = "hypertuneCookieConsent";
var backendGraphqlEndpoint = "/graphql";
var teamGroupId = "team";
var hashRequestLogSamplingRate = 1 / 1e3;
var oldSchemaWelcomeMessage = `# Welcome to Hypertune, the most powerful feature flag, A/B testing and app 
# configuration platform.
#
# Follow the quickstart: https://docs.hypertune.com/quickstart
#
# This is your schema, written in GraphQL. Use Boolean for feature flags, 
# custom enums for flags with more than two states, Int for numeric flags like 
# limits and timeouts, Strings for in-app copy, and custom object and list types
# for more complex app configuration.
#
# Once you've defined your schema, head to the Logic tab.
`;
var newSchemaWelcomeMessage = `"""
Welcome to Hypertune, the most powerful feature flag, A/B testing and app
configuration platform.

Follow the quickstart: https://docs.hypertune.com/quickstart

This is your schema, written in GraphQL. Use Boolean for feature flags,
custom enums for flags with more than two states, Int for numeric flags like
limits and timeouts, Strings for in-app copy, and custom object and list types
for more complex app configuration.

Once you've defined your schema, head to the Logic tab.
"""`;
var oldSchemaRootComment = `# You can add arguments to any field in your schema, which you can then
  # reference when defining your logic. We've added a 'context' argument on your
  # root field already, which contains details of the current 'user'.`;
var newSchemaRootComment = `"""
  You can add arguments to any field in your schema, which you can then
  reference when defining your logic. We've added a 'context' argument on your
  root field already, which contains details of the current 'user'.
  """`;
var variablePathSuffix = "-var";
var vercelFlagPathSeparator = ".";
var plansPath = "/plans";

// src/schema/isValidGraphqlInputType.ts
function isValidGraphqlInputType(inputType) {
  if (!isNonNullType(inputType)) {
    return false;
  }
  const innerType = inputType.ofType;
  if (isListType(innerType)) {
    return isValidGraphqlInputType(innerType.ofType);
  }
  return isScalarType(innerType) && innerType.name !== voidGraphqlScalarTypeName || isEnumType(innerType) || isInputObjectType(innerType);
}

// src/schema/getGraphqlSchema.ts
var internalTypeName = "__Internal";
var internalTypeNames = /* @__PURE__ */ new Set([
  "__Tag",
  internalTypeName,
  ...introspectionTypes.map((type) => type.name)
]);
function getGraphqlSchema(schemaCode) {
  const graphqlSchema = buildSchema(
    addScalarTypesAndDirectivesToSchemaCode(schemaCode)
  );
  validateGraphqlSchema(graphqlSchema);
  return graphqlSchema;
}
function addScalarTypesAndDirectivesToSchemaCode(code) {
  return `${graphqlScalarTypeNames.map((s) => `scalar ${s}`).join("\n")}
    
  directive @event on INPUT_OBJECT
    
  directive @data(tags: [__Tag]!) on OBJECT
    
  type __Tag {
    name: String!
    color: String!
  }
  
  
  ${code}`;
}
function validateGraphqlSchema(graphqlSchema) {
  const queryType = graphqlSchema.getQueryType();
  if (!queryType) {
    throw new Error(`You must have a root "${queryObjectTypeName}" type.`);
  }
  const queryTypeFieldNames = Object.keys(queryType.getFields());
  if (queryTypeFieldNames.length !== 1 || queryTypeFieldNames[0] !== rootFieldName) {
    throw new Error(
      `The "${queryObjectTypeName}" type must have exactly one field called "${rootFieldName}".`
    );
  }
  const typeMap = graphqlSchema.getTypeMap();
  Object.keys(typeMap).forEach((typeName) => {
    if (typeName.includes("_") && !internalTypeNames.has(typeName)) {
      throw new Error(
        `You cannot use underscores in type names. Please rename type "${typeName}".`
      );
    }
    if (typeName.startsWith("__")) {
      return;
    }
    if (reservedTypeNames.includes(typeName)) {
      throw new Error(
        `Type name "${typeName}" is reserved. Please rename this type.`
      );
    }
    const type = typeMap[typeName];
    if (isScalarType(type)) {
      if (!graphqlScalarTypeNames.includes(type.name)) {
        throw new Error(`Unsupported scalar type: "${type.name}"`);
      }
    }
    if (isEnumType(type)) {
      if (type.getValues().length === 0) {
        throw new Error(
          `You must add at least one value to the enum "${typeName}".`
        );
      }
    }
    if (isObjectType(type)) {
      const fieldMap = type.getFields();
      const fieldNames = Object.keys(fieldMap);
      fieldNames.forEach((fieldName) => {
        const field = fieldMap[fieldName];
        if (field.name.startsWith("__")) {
          throw new Error(
            `You cannot start field names with '__'. Please rename field "${field.name}".`
          );
        }
        if (!isNonNullType(field.type)) {
          throw new Error(
            `All fields must have a non-null type. Append '!' to the type of field "${field.name}" of object type "${typeName}".`
          );
        }
        if (isInputObjectType(field.type.ofType)) {
          throw new Error(
            `Invalid type for field "${fieldName}" of object type "${typeName}". Input types cannot be used in output types.`
          );
        }
        field.args.forEach((arg) => {
          if (!isValidGraphqlInputType(arg.type)) {
            throw new Error(
              `Invalid type for argument "${arg.name}" of field "${fieldName}" of object type "${typeName}". Field arguments must be non-nullable scalars, enums or input object types.`
            );
          }
        });
      });
    }
    if (isUnionType(type)) {
      const types = type.getTypes();
      if (types.length === 0) {
        throw new Error(
          `Union type "${type.name}" must have at least one member.`
        );
      }
      types.forEach((member) => {
        if (!isObjectType(member)) {
          throw new Error(
            `The "${type.name}" union type contains an invalid member "${member.toString()}". Union types can only contain object types.`
          );
        }
      });
    }
    if (isInterfaceType(type)) {
      throw new Error(`Interface types like "${typeName}" are not allowed.`);
    }
    if (isInputObjectType(type)) {
      const isEvent = isEventType(type);
      const fieldMap = type.getFields();
      Object.keys(fieldMap).forEach((fieldName) => {
        const field = fieldMap[fieldName];
        if (field.name.startsWith("__")) {
          throw new Error(
            `You cannot start field names with '__'. Please rename field ${field.name}`
          );
        }
        if (!isNonNullType(field.type)) {
          throw new Error(
            `All fields must have a non-null type. Append '!' to the type of field "${field.name}" of input object type "${typeName}".`
          );
        }
        if (!isValidGraphqlInputType(field.type)) {
          throw new Error(
            `Invalid type for field "${fieldName}" of input object type "${typeName}". Input object fields must be non-nullable scalars, enums or input object types.`
          );
        }
        if (field.deprecationReason) {
          throw new Error(
            `Deprecated field "${fieldName}" of input object type "${typeName}". Fields of input objects can't be marked as deprecated, delete them instead.`
          );
        }
        if (!isEvent && isInputObjectType(field.type.ofType) && isEventType(field.type.ofType)) {
          throw new Error(
            `Invalid type for field "${fieldName}" of input object type "${typeName}". Input object fields can't be events.`
          );
        }
      });
    }
  });
}
function isEventType(type) {
  return (isInputObjectType(type) && type.astNode?.directives?.some(
    (directive) => directive.name.value === "event"
  )) ?? false;
}
function makeGraphqlSchemaInputTypesOptional(graphqlSchema) {
  Object.entries(graphqlSchema.getTypeMap()).forEach(([typeName, type]) => {
    if (isObjectType(type)) {
      Object.entries(type.getFields()).forEach(([fieldName, field]) => {
        field.args.forEach((arg) => {
          arg.type = getOptionalInputType(arg.type);
        });
      });
    }
    if (isInputObjectType(type)) {
      Object.entries(type.getFields()).forEach(([fieldName, field]) => {
        field.type = getOptionalInputType(field.type);
      });
    }
  });
}
function getOptionalInputType(inputType) {
  if (isNonNullType(inputType)) {
    return getOptionalInputType(inputType.ofType);
  }
  if (isListType(inputType)) {
    return new GraphQLList(getOptionalInputType(inputType.ofType));
  }
  return inputType;
}
function getValueTypeFromGraphqlType(graphqlType) {
  if (!isNonNullType(graphqlType)) {
    throw new Error(`GraphQL type "${graphqlType.name}" must be non-nullable.`);
  }
  const innerType = graphqlType.ofType;
  if (isScalarType(innerType)) {
    const scalarTypeName = innerType.name;
    switch (scalarTypeName) {
      case "Void":
        return { type: "VoidValueType" };
      case "Boolean":
        return { type: "BooleanValueType" };
      case "Int":
        return { type: "IntValueType" };
      case "Float":
        return { type: "FloatValueType" };
      case "String":
        return { type: "StringValueType" };
      default: {
        const neverScalarTypeName = scalarTypeName;
        throw new Error(`Unexpected scalar type name: ${neverScalarTypeName}`);
      }
    }
  }
  if (isEnumType(innerType)) {
    return {
      type: "EnumValueType",
      enumTypeName: innerType.name
    };
  }
  if (isObjectType(innerType) || isInputObjectType(innerType)) {
    return {
      type: "ObjectValueType",
      objectTypeName: innerType.name
    };
  }
  if (isUnionType(innerType)) {
    return {
      type: "UnionValueType",
      unionTypeName: innerType.name
    };
  }
  if (isInterfaceType(innerType)) {
    throw new Error("Unexpected interface type");
  }
  if (isListType(innerType)) {
    return {
      type: "ListValueType",
      itemValueType: getValueTypeFromGraphqlType(innerType.ofType)
    };
  }
  const neverGraphqlOutputType = innerType;
  throw new Error(
    `No value type conversion implementation for GraphQL output type: ${neverGraphqlOutputType}`
  );
}

// src/schema/valueTypeToString.ts
function valueTypeToString(valueType) {
  switch (valueType.type) {
    case "VoidValueType":
      return "Void";
    case "BooleanValueType":
      return "Boolean";
    case "IntValueType":
      return "Int";
    case "FloatValueType":
      return "Float";
    case "StringValueType":
      return "String";
    case "RegexValueType":
      return "Regex";
    case "EnumValueType":
      return valueType.enumTypeName;
    case "ObjectValueType":
      return valueType.objectTypeName;
    case "UnionValueType":
      return valueType.unionTypeName;
    case "ListValueType":
      return `List[${valueTypeToString(valueType.itemValueType)}]`;
    case "FunctionValueType":
      return `(${valueType.parameterValueTypes.map((parameterValueType) => valueTypeToString(parameterValueType)).join(", ")}) ${rightDoubleArrowSymbol} ${valueTypeToString(
        valueType.returnValueType
      )}`;
    default: {
      const neverValueType = valueType;
      throw new Error(
        `valueTypeToStringInner unexpected value type: ${JSON.stringify(
          neverValueType
        )}`
      );
    }
  }
}
function getSchema(graphqlSchema) {
  const enums = {};
  const objects = {};
  const unions = {};
  const tags = {};
  const typeMap = graphqlSchema.getTypeMap();
  Object.keys(typeMap).forEach((typeName) => {
    const type = typeMap[typeName];
    if (typeName === "__Internal") {
      if (type.name === "__Internal" && type.astNode?.directives && type.astNode?.directives?.length > 0 && type.astNode?.directives[0].name.value === "data" && type.astNode?.directives[0]?.arguments && type.astNode?.directives[0]?.arguments[0]?.value && type.astNode?.directives[0]?.arguments[0]?.value?.kind === "ListValue") {
        type.astNode?.directives[0]?.arguments[0]?.value.values.map((value) => {
          if (value?.kind === "ObjectValue" && value.fields?.length === 2 && value.fields[0].value.kind === "StringValue" && value.fields[1].value.kind === "StringValue") {
            tags[value.fields[0].value.value] = {
              name: value.fields[0].value.value,
              color: value.fields[1].value.value
            };
          }
          return null;
        });
      }
    }
    if (typeName.startsWith("__")) {
      return;
    }
    if (isEnumType(type)) {
      enums[type.name] = {
        description: type.description || null,
        values: Object.fromEntries(
          type.getValues().map((value) => [
            value.name,
            {
              description: value.description || null,
              deprecationReason: value.deprecationReason === null ? void 0 : value.deprecationReason
            }
          ])
        )
      };
    }
    if (isInputObjectType(type)) {
      const schemaInputObjectFields = {};
      const fieldMap = type.getFields();
      Object.keys(fieldMap).forEach((fieldName) => {
        const field = fieldMap[fieldName];
        schemaInputObjectFields[fieldName] = {
          description: field.description || null,
          deprecationReason: field.deprecationReason === null ? void 0 : field.deprecationReason,
          valueType: getValueTypeFromGraphqlType(field.type)
        };
      });
      objects[type.name] = {
        role: isEventType(type) ? "event" : "input",
        description: type.description || null,
        fields: schemaInputObjectFields
      };
    }
    if (isObjectType(type)) {
      const schemaObjectFields = {};
      const fieldMap = type.getFields();
      Object.keys(fieldMap).forEach((fieldName) => {
        const field = fieldMap[fieldName];
        const schemaFieldArgumentsObjectFields = {};
        field.args.forEach((arg) => {
          schemaFieldArgumentsObjectFields[arg.name] = {
            description: arg.description || null,
            valueType: getValueTypeFromGraphqlType(arg.type)
          };
        });
        const fieldArgumentsObjectTypeName = getFieldArgumentsObjectTypeName({
          parentObjectTypeName: type.name,
          fieldName
        });
        objects[fieldArgumentsObjectTypeName] = {
          role: "args",
          description: null,
          fields: schemaFieldArgumentsObjectFields
        };
        schemaObjectFields[fieldName] = {
          description: field.description || null,
          deprecationReason: field.deprecationReason === null ? void 0 : field.deprecationReason,
          valueType: {
            type: "FunctionValueType",
            parameterValueTypes: [
              {
                type: "ObjectValueType",
                objectTypeName: fieldArgumentsObjectTypeName
              }
            ],
            returnValueType: getValueTypeFromGraphqlType(field.type)
          }
        };
      });
      objects[type.name] = {
        role: "output",
        description: type.description || null,
        fields: schemaObjectFields
      };
    }
    if (isUnionType(type)) {
      unions[type.name] = {
        description: type.description || null,
        variants: Object.fromEntries(
          type.getTypes().map((objectType) => [objectType.name, true])
        )
      };
    }
  });
  return { enums, objects, unions, tags };
}

// src/schema/valueTypesAreEqual.ts
function valueTypesAreEqual(a, b) {
  if (a.type !== b.type) {
    return false;
  }
  switch (a.type) {
    case "VoidValueType":
    case "BooleanValueType":
    case "IntValueType":
    case "FloatValueType":
    case "StringValueType":
    case "RegexValueType":
      return true;
    case "EnumValueType":
      return a.type === b.type && a.enumTypeName === b.enumTypeName;
    case "ObjectValueType":
      return a.type === b.type && a.objectTypeName === b.objectTypeName;
    case "UnionValueType":
      return a.type === b.type && a.unionTypeName === b.unionTypeName;
    case "ListValueType":
      return a.type === b.type && valueTypesAreEqual(a.itemValueType, b.itemValueType);
    case "FunctionValueType":
      return a.type === b.type && valueTypesAreEqual(a.returnValueType, b.returnValueType) && a.parameterValueTypes.length === b.parameterValueTypes.length && a.parameterValueTypes.every(
        (aParameterValueType, index) => valueTypesAreEqual(aParameterValueType, b.parameterValueTypes[index])
      );
    default: {
      const neverValueType = a;
      throw new Error(`unexpected value type: ${neverValueType}`);
    }
  }
}
function getDefaultValue(schema, valueType) {
  switch (valueType.type) {
    case "BooleanValueType":
      return false;
    case "IntValueType":
    case "FloatValueType":
      return 0;
    case "StringValueType":
      return "";
    case "EnumValueType": {
      const enumValues = Object.keys(
        nullThrows2(
          schema.enums[valueType.enumTypeName].values,
          `No schema enum "${valueType.enumTypeName}".`
        )
      );
      if (enumValues.length === 0) {
        throw new Error(`No enum values for enum "${valueType.enumTypeName}".`);
      }
      return enumValues[0];
    }
    case "ObjectValueType": {
      const schemaObject = nullThrows2(
        schema.objects[valueType.objectTypeName],
        `No schema object "${valueType.objectTypeName}"`
      );
      return Object.fromEntries(
        Object.entries(schemaObject.fields).map(([fieldName, field]) => [
          fieldName,
          getDefaultValue(schema, field.valueType)
        ])
      );
    }
    case "ListValueType":
      return [];
    case "UnionValueType": {
      const unionValues = Object.keys(
        nullThrows2(
          schema.unions[valueType.unionTypeName],
          `No schema union "${valueType.unionTypeName}".`
        )
      );
      if (unionValues.length === 0) {
        throw new Error(`No union values for "${valueType.unionTypeName}".`);
      }
      return getDefaultValue(schema, {
        type: "ObjectValueType",
        objectTypeName: unionValues[0]
      });
    }
    case "VoidValueType":
    case "RegexValueType":
    case "FunctionValueType":
      throw new Error(`Unexpected value type: ${valueType.type}`);
    default: {
      const neverValueType = valueType;
      throw new Error(
        `Unexpected value type: ${JSON.stringify(neverValueType)}`
      );
    }
  }
}

// src/schema/getDefaultQuery.ts
function getDefaultQuery({
  schema,
  includeDeprecated,
  includeArguments,
  markQueryFieldArgumentsPartial,
  useSharedFragments,
  objectTypeNames
}) {
  if (objectTypeNames.length === 0) {
    return null;
  }
  const fragmentDefinitions = {};
  const fieldQuery = getDefaultFieldQuery({
    schema,
    includeDeprecated,
    includeArguments,
    markQueryFieldArgumentsPartial,
    useSharedFragments,
    fragmentDefinitions,
    objectTypeNames
  }) ?? {};
  if (Object.keys(fieldQuery).length === 0) {
    return null;
  }
  return {
    name: "FullQuery",
    variableDefinitions: {},
    fragmentDefinitions: Object.fromEntries(
      Object.entries(fragmentDefinitions).filter(
        ([, fragment]) => Object.keys(fragment.selection).length > 0
      )
    ),
    fieldQuery
  };
}
function getDefaultFieldQuery({
  schema,
  includeDeprecated,
  includeArguments,
  markQueryFieldArgumentsPartial,
  useSharedFragments,
  fragmentDefinitions,
  objectTypeNames
}) {
  if (objectTypeNames.length === 0) {
    return null;
  }
  const query = Object.fromEntries(
    objectTypeNames.map((objectTypeName) => {
      const inlineFragment = {
        type: "InlineFragment",
        objectTypeName,
        selection: Object.fromEntries(
          Object.entries(schema.objects[objectTypeName]?.fields || {}).filter(
            ([, field]) => includeDeprecated || field.deprecationReason === void 0
          ).flatMap(([fieldName, field]) => {
            const fieldValueType = field.valueType;
            if (fieldValueType.type !== "FunctionValueType") {
              return [];
            }
            const fieldQuery = getDefaultFieldQuery({
              schema,
              includeDeprecated,
              includeArguments,
              markQueryFieldArgumentsPartial,
              useSharedFragments,
              fragmentDefinitions,
              objectTypeNames: getObjectTypeNames(
                schema,
                fieldValueType.returnValueType
              )
            });
            if (fieldValueType.returnValueType.type === "ObjectValueType" && fieldQuery === null) {
              return [];
            }
            const argsValueType = fieldValueType.parameterValueTypes[0];
            return [
              [
                fieldName,
                {
                  fieldArguments: includeArguments && fieldValueType.parameterValueTypes.length === 1 ? (
                    // We know the top element is an object,
                    // so we can just cast to the desired type.
                    getDefaultValue(
                      schema,
                      argsValueType
                    )
                  ) : markQueryFieldArgumentsPartial && fieldValueType.parameterValueTypes.length === 1 && argsValueType.type === "ObjectValueType" && Object.keys(
                    schema.objects[argsValueType.objectTypeName].fields
                  ).length > 0 ? (
                    // Mark fieldArguments as partial if the field has any arguments.
                    { [isPartialObjectKey$1]: true }
                  ) : {},
                  fieldQuery
                }
              ]
            ];
          })
        )
      };
      if (useSharedFragments && objectTypeName !== queryObjectTypeName && objectTypeName !== rootObjectTypeName) {
        if (useSharedFragments && !fragmentDefinitions[objectTypeName]) {
          fragmentDefinitions[objectTypeName] = inlineFragment;
        }
        return [
          objectTypeName,
          {
            type: "FragmentSpread",
            fragmentName: objectTypeName
          }
        ];
      }
      return [objectTypeName, inlineFragment];
    }).filter(
      ([objectTypeName, fragment]) => fragment.type === "FragmentSpread" && Object.keys(fragmentDefinitions[objectTypeName].selection).length > 0 || fragment.type === "InlineFragment" && Object.keys(fragment.selection).length > 0
    )
  );
  if (Object.keys(query).length === 0) {
    return null;
  }
  return query;
}
function getObjectTypeNames(schema, returnValueType) {
  switch (returnValueType.type) {
    case "ObjectValueType":
      return [returnValueType.objectTypeName];
    case "ListValueType":
      return getObjectTypeNames(schema, returnValueType.itemValueType);
    case "UnionValueType": {
      return Object.keys(
        schema.unions[returnValueType.unionTypeName].variants || {}
      );
    }
    default:
      return [];
  }
}

// src/getSchemaAndQuery.ts
var allFieldsKey = "__allNestedFields__";
function getSchemaAndQuery({
  graphqlSchema,
  queryCode,
  silentlySkipInvalidFieldNames,
  markQueryFieldArgumentsPartial,
  useSharedFragments
}) {
  const document = parse(queryCode.replaceAll("*", allFieldsKey));
  const queryDefinitions = [];
  const graphqlFragmentDefinitions = {};
  document.definitions.forEach((definition) => {
    const { kind } = definition;
    switch (definition.kind) {
      case Kind.OPERATION_DEFINITION: {
        if (definition.operation !== "query") {
          throw new Error("You can only define a query operation");
        }
        queryDefinitions.push(definition);
        return;
      }
      case Kind.FRAGMENT_DEFINITION: {
        graphqlFragmentDefinitions[definition.name.value] = definition;
        return;
      }
      default: {
        throw new Error(`Unexpected definition kind: ${kind}`);
      }
    }
  });
  if (queryDefinitions.length !== 1) {
    throw new Error("You must define exactly 1 query");
  }
  const queryDefinition = queryDefinitions[0];
  const queryName = queryDefinition.name?.value;
  const { variableDefinitions } = getVariableDefinitions(
    graphqlSchema,
    queryDefinition.variableDefinitions ?? []
  );
  const schema = getSchema(graphqlSchema);
  const query = getQueryFromSelectionSet({
    schema,
    silentlySkipInvalidFieldNames,
    markQueryFieldArgumentsPartial,
    useSharedFragments,
    graphqlFragmentDefinitions,
    selectionSet: queryDefinition.selectionSet,
    valueType: { type: "ObjectValueType", objectTypeName: queryObjectTypeName },
    variableDefinitions
  });
  return { schema, query, queryName };
}
function getQueryFromSelectionSet({
  schema,
  silentlySkipInvalidFieldNames,
  markQueryFieldArgumentsPartial,
  useSharedFragments,
  graphqlFragmentDefinitions,
  selectionSet,
  valueType,
  variableDefinitions
}) {
  const fragmentDefinitions = {};
  return {
    variableDefinitions,
    fragmentDefinitions: useSharedFragments ? fragmentDefinitions : {},
    fieldQuery: getFieldQueryFromSelectionSet({
      schema,
      silentlySkipInvalidFieldNames,
      markQueryFieldArgumentsPartial,
      useSharedFragments,
      fragmentDefinitions,
      graphqlFragmentDefinitions,
      selectionSet,
      valueType,
      variableDefinitions
    })
  };
}
function getFieldQueryFromSelectionSet({
  schema,
  silentlySkipInvalidFieldNames,
  markQueryFieldArgumentsPartial,
  useSharedFragments,
  fragmentDefinitions,
  graphqlFragmentDefinitions,
  selectionSet,
  valueType,
  variableDefinitions
}) {
  const query = {};
  selectionSet.selections.forEach((selection) => {
    const { kind } = selection;
    switch (kind) {
      case Kind.FIELD: {
        if (selection.alias) {
          throw new Error("Field aliases are not supported");
        }
        if (valueType.type === "UnionValueType") {
          throw new Error(
            `You cannot directly select subfields on union type "${valueType.unionTypeName}". You can use inline or named fragments on the concrete types in the union instead.`
          );
        }
        const { objectTypeName } = valueType;
        const schemaObject = nullThrows2(
          schema.objects[objectTypeName]?.fields,
          `No concrete type "${objectTypeName}".`
        );
        const fieldName = selection.name.value;
        if (fieldName === graphqlTypeNameKey) {
          return;
        }
        if (fieldName === allFieldsKey) {
          const fullQuery = nullThrows2(
            getDefaultQuery({
              schema,
              includeDeprecated: false,
              includeArguments: false,
              markQueryFieldArgumentsPartial,
              objectTypeNames: [objectTypeName],
              useSharedFragments: (
                // Only use shared fragments if there are no fragments
                // specified in the GraphQL to prevent potential collisions.
                useSharedFragments && Object.keys(graphqlFragmentDefinitions).length === 0
              )
            }),
            new Error(`No default query for type "${objectTypeName}".`)
          );
          Object.assign(query, fullQuery.fieldQuery);
          Object.assign(fragmentDefinitions, fullQuery.fragmentDefinitions);
          return;
        }
        if (!query[objectTypeName]) {
          query[objectTypeName] = {
            type: "InlineFragment",
            objectTypeName,
            selection: {}
          };
        }
        const fieldValueType = schemaObject[fieldName]?.valueType;
        if (!fieldValueType) {
          if (silentlySkipInvalidFieldNames) {
            return;
          }
          throw new Error(
            `No field "${fieldName}" on type "${objectTypeName}".`
          );
        }
        if (fieldValueType.type !== "FunctionValueType") {
          throw new Error("Not a function value type.");
        }
        const fieldArguments = Object.fromEntries(
          (selection.arguments || []).flatMap((argument) => {
            const value = getValue(argument.value);
            return typeof value === "undefined" ? [] : [[argument.name.value, value]];
          })
        );
        const { errorMessages, missingKeyErrorMessages } = getValueErrorMessages(
          schema,
          {
            type: "ObjectValueType",
            objectTypeName: getFieldArgumentsObjectTypeName({
              parentObjectTypeName: objectTypeName,
              fieldName
            })
          },
          fieldArguments,
          variableDefinitions,
          [objectTypeName, fieldName]
        );
        if (errorMessages.length > 0) {
          throw new Error(`Errors: ${errorMessages.join(" ")}`);
        }
        if (missingKeyErrorMessages.length > 0) {
          if (markQueryFieldArgumentsPartial) {
            fieldArguments[isPartialObjectKey] = true;
          }
        }
        const nestedReturnObjectOrUnionValueType = getNestedObjectOrUnionValueType(fieldValueType.returnValueType);
        query[objectTypeName].selection[fieldName] = {
          fieldArguments,
          fieldQuery: selection.selectionSet ? getFieldQueryFromSelectionSet({
            schema,
            silentlySkipInvalidFieldNames,
            markQueryFieldArgumentsPartial,
            useSharedFragments,
            graphqlFragmentDefinitions,
            fragmentDefinitions,
            selectionSet: selection.selectionSet,
            valueType: nestedReturnObjectOrUnionValueType || throws(
              `You cannot select subfields on field "${objectTypeName}.${fieldName}" with return type "${valueTypeToString(
                fieldValueType.returnValueType
              )}".`
            ),
            variableDefinitions
          }) : nestedReturnObjectOrUnionValueType ? throws(
            `You must select subfields on field "${objectTypeName}.${fieldName}" with return type "${valueTypeToString(
              fieldValueType.returnValueType
            )}".`
          ) : null
        };
        break;
      }
      case Kind.FRAGMENT_SPREAD: {
        const fragmentName = selection.name.value;
        const fragment = nullThrows2(
          graphqlFragmentDefinitions[fragmentName],
          `No fragment with name "${fragmentName}".`
        );
        const objectTypeName = fragment.typeCondition.name.value;
        if (valueType.type === "UnionValueType") {
          validateUnionAndMember(schema, valueType, objectTypeName);
        }
        if (!fragmentDefinitions[fragmentName]) {
          const fragmentQuery = getFieldQueryFromSelectionSet({
            schema,
            silentlySkipInvalidFieldNames,
            markQueryFieldArgumentsPartial,
            useSharedFragments,
            graphqlFragmentDefinitions,
            fragmentDefinitions,
            selectionSet: fragment.selectionSet,
            valueType: { type: "ObjectValueType", objectTypeName },
            variableDefinitions
          });
          const [fragmentDefinition] = fragmentQuery ? Object.values(fragmentQuery) : [null];
          if (fragmentDefinition?.type !== "InlineFragment") {
            throw new Error(
              `invalid fragment definition: "${JSON.stringify({ fragmentDefinition, selectionSet })}"`
            );
          }
          fragmentDefinitions[fragmentName] = fragmentDefinition;
        }
        query[objectTypeName] = useSharedFragments ? {
          type: "FragmentSpread",
          fragmentName
        } : fragmentDefinitions[fragmentName];
        break;
      }
      case Kind.INLINE_FRAGMENT: {
        const objectTypeName = selection.typeCondition ? selection.typeCondition.name.value : valueType.type === "ObjectValueType" ? valueType.objectTypeName : throws(
          `You cannot have an inline fragment with no type condition directly on a union type like "${valueType.unionTypeName}". Inline fragments on unions must have a concrete type condition.`
        );
        if (valueType.type === "UnionValueType") {
          validateUnionAndMember(schema, valueType, objectTypeName);
        }
        const fragmentQuery = getFieldQueryFromSelectionSet({
          schema,
          silentlySkipInvalidFieldNames,
          markQueryFieldArgumentsPartial,
          useSharedFragments,
          graphqlFragmentDefinitions,
          fragmentDefinitions,
          selectionSet: selection.selectionSet,
          valueType: { type: "ObjectValueType", objectTypeName },
          variableDefinitions
        });
        Object.assign(query, fragmentQuery);
        break;
      }
      default: {
        const neverKind = kind;
        throw new Error(`Unexpected selection kind: ${neverKind}`);
      }
    }
  });
  return query;
}
function getValue(value) {
  const { kind } = value;
  switch (kind) {
    case Kind.BOOLEAN:
    case Kind.STRING:
    case Kind.ENUM:
      return value.value;
    case Kind.INT: {
      return parseInt(value.value, 10);
    }
    case Kind.FLOAT: {
      return parseFloat(value.value);
    }
    case Kind.OBJECT:
      return Object.fromEntries(
        value.fields.map((field) => [field.name.value, getValue(field.value)])
      );
    case Kind.LIST:
      return value.values.map((item) => getValue(item));
    case Kind.NULL:
      throw new Error("Field argument values cannot be null.");
    case Kind.VARIABLE: {
      const queryVariable = {
        [isQueryVariableKey]: true,
        name: value.name.value
      };
      return queryVariable;
    }
    default: {
      const neverKind = kind;
      throw new Error(`Unexpected value kind: ${neverKind}`);
    }
  }
}
function getValueErrorMessages(schema, expectedValueType, actualValue, variableDefinitions, path) {
  const errorMessage = `Expected a ${valueTypeToString(
    expectedValueType
  )} but got ${JSON.stringify(
    actualValue
  )} (${typeof actualValue}) for field argument "${path.join(".")}".${expectedValueType.type === "EnumValueType" ? ` The valid values are ${Object.keys(
    nullThrows2(
      schema.enums[expectedValueType.enumTypeName].values,
      `No schema enum "${expectedValueType.enumTypeName}"`
    )
  ).join(", ")}.` : ""}`;
  if (isQueryVariable(actualValue)) {
    const actualVariableValueType = variableDefinitions[actualValue.name];
    return {
      errorMessages: valueTypesAreEqual(
        actualVariableValueType.valueType,
        expectedValueType
      ) ? [] : [
        `Expected a ${valueTypeToString(
          expectedValueType
        )} but got a variable of type ${valueTypeToString(
          actualVariableValueType.valueType
        )} for field argument "${path.join(".")}".`
      ],
      missingKeyErrorMessages: []
    };
  }
  switch (expectedValueType.type) {
    case "BooleanValueType":
      return {
        errorMessages: typeof actualValue === "boolean" ? [] : [errorMessage],
        missingKeyErrorMessages: []
      };
    case "IntValueType":
      return {
        errorMessages: typeof actualValue === "number" && Number.isInteger(actualValue) ? [] : [errorMessage],
        missingKeyErrorMessages: []
      };
    case "FloatValueType":
      return {
        errorMessages: typeof actualValue === "number" ? [] : [errorMessage],
        missingKeyErrorMessages: []
      };
    case "StringValueType":
      return {
        errorMessages: typeof actualValue === "string" ? [] : [errorMessage],
        missingKeyErrorMessages: []
      };
    case "EnumValueType": {
      const schemaEnum = nullThrows2(
        schema.enums[expectedValueType.enumTypeName].values,
        `No schema enum "${expectedValueType.enumTypeName}"`
      );
      return {
        errorMessages: typeof actualValue === "string" && !!schemaEnum[actualValue] ? [] : [errorMessage],
        missingKeyErrorMessages: []
      };
    }
    case "ListValueType": {
      if (!Array.isArray(actualValue)) {
        return { errorMessages: [errorMessage], missingKeyErrorMessages: [] };
      }
      const errorMessages = [];
      actualValue.forEach((item, index) => {
        const itemResult = getValueErrorMessages(
          schema,
          expectedValueType.itemValueType,
          item,
          variableDefinitions,
          [...path, `[${index}]`]
        );
        errorMessages.push(
          ...itemResult.errorMessages,
          ...itemResult.missingKeyErrorMessages
        );
      });
      return { errorMessages, missingKeyErrorMessages: [] };
    }
    case "ObjectValueType": {
      const schemaObject = nullThrows2(
        schema.objects[expectedValueType.objectTypeName],
        `No schema object "${expectedValueType.objectTypeName}"`
      );
      if (!actualValue || typeof actualValue !== "object" || Array.isArray(actualValue)) {
        return { errorMessages: [errorMessage], missingKeyErrorMessages: [] };
      }
      const errorMessages = [];
      const missingKeyErrorMessages = [];
      Object.entries(schemaObject.fields).forEach(([fieldName, field]) => {
        const fieldValueType = field.valueType;
        const fieldPath = [...path, fieldName];
        const fieldValue = actualValue[fieldName];
        if (typeof fieldValue === "undefined") {
          missingKeyErrorMessages.push(
            `Missing field argument "${fieldPath.join(
              "."
            )}" with expected type "${valueTypeToString(fieldValueType)}".`
          );
          return;
        }
        const fieldResult = getValueErrorMessages(
          schema,
          fieldValueType,
          fieldValue,
          variableDefinitions,
          fieldPath
        );
        errorMessages.push(...fieldResult.errorMessages);
        missingKeyErrorMessages.push(...fieldResult.missingKeyErrorMessages);
      });
      Object.keys(actualValue).forEach((fieldName) => {
        if (!schemaObject.fields[fieldName]) {
          errorMessages.push(
            `Extra field argument "${[...path, fieldName].join(".")}".`
          );
        }
      });
      return { errorMessages, missingKeyErrorMessages };
    }
    case "VoidValueType":
    case "RegexValueType":
    case "UnionValueType":
    case "FunctionValueType":
      throw new Error(`unexpected value type: ${expectedValueType.type}`);
    default: {
      const neverValueType = expectedValueType;
      throw new Error(`unexpected value type: ${neverValueType}`);
    }
  }
}
function getNestedObjectOrUnionValueType(valueType) {
  if (valueType.type === "ObjectValueType" || valueType.type === "UnionValueType") {
    return valueType;
  }
  if (valueType.type === "ListValueType") {
    return getNestedObjectOrUnionValueType(valueType.itemValueType);
  }
  return null;
}
function validateUnionAndMember(schema, unionValueType, memberTypeName) {
  const schemaUnion = nullThrows2(
    schema.unions[unionValueType.unionTypeName]?.variants,
    `No union type "${unionValueType.unionTypeName}".`
  );
  if (!schemaUnion[memberTypeName]) {
    throw new Error(
      `Union type "${unionValueType.unionTypeName}" has no member "${memberTypeName}".`
    );
  }
}
function getVariableDefinitions(graphqlSchema, graphqlVariableDefinitions) {
  const variableDefinitions = {};
  graphqlVariableDefinitions.forEach((variableDefinition) => {
    const variableName = variableDefinition.variable.name.value;
    const graphqlType = nullThrows2(
      getGraphqlTypeFromAstType(graphqlSchema, variableDefinition.type),
      `Variable "${variableName}" has invalid GraphQL type.`
    );
    if (!isInputType(graphqlType)) {
      throw new Error(
        `GraphQL type for variable "${variableName}" is not an input type.`
      );
    }
    if (!isValidGraphqlInputType(graphqlType)) {
      throw new Error(
        `Invalid type for variable "${variableName}". Variable types must be non-nullable scalars, enums or input object types.`
      );
    }
    const variableValueType = getValueTypeFromGraphqlType(graphqlType);
    variableDefinitions[variableName] = {
      valueType: variableValueType,
      defaultValue: variableDefinition.defaultValue ? getDefaultVariableValue(variableDefinition.defaultValue) : void 0
    };
  });
  return { variableDefinitions };
}
function getDefaultVariableValue(defaultValue) {
  switch (defaultValue.kind) {
    case Kind.BOOLEAN:
      return defaultValue.value;
    case Kind.STRING:
      return defaultValue.value;
    case Kind.INT:
      return parseInt(defaultValue.value);
    case Kind.FLOAT:
      return parseFloat(defaultValue.value);
    case Kind.ENUM:
      return defaultValue.value;
    case Kind.LIST:
      return defaultValue.values.map((value) => getDefaultVariableValue(value));
    case Kind.OBJECT:
      return Object.fromEntries(
        defaultValue.fields.map(({ name, value }) => [
          name,
          getDefaultVariableValue(value)
        ])
      );
    default:
      throw new Error(
        `Unexpected query default value type: ${defaultValue.kind}`
      );
  }
}
function getGraphqlTypeFromAstType(graphqlSchema, astType) {
  const { kind } = astType;
  switch (kind) {
    case Kind.NAMED_TYPE:
      return typeFromAST(graphqlSchema, astType);
    case Kind.LIST_TYPE:
      return typeFromAST(graphqlSchema, astType);
    case Kind.NON_NULL_TYPE:
      return typeFromAST(graphqlSchema, astType);
    default: {
      const neverKind = kind;
      throw new Error(`Unexpected type kind: ${neverKind}`);
    }
  }
}
function getUserAgent(userAgentString) {
  const userAgent = UAParser(userAgentString || "none");
  return {
    browser: {
      name: userAgent.browser.name || "",
      version: userAgent.browser.version || ""
    },
    device: {
      type: userAgent.device.type || "",
      vendor: userAgent.device.vendor || "",
      model: userAgent.device.model || ""
    },
    engine: {
      name: userAgent.engine.name || "",
      version: userAgent.engine.version || ""
    },
    os: {
      name: userAgent.os.name || "",
      version: userAgent.os.version || ""
    },
    cpu: {
      architecture: userAgent.cpu.architecture || ""
    }
  };
}

// src/replaceVariablePlaceholders.ts
function replaceVariablePlaceholders(variableValues, userAgent, referer) {
  const ua = getUserAgent(userAgent);
  return Object.fromEntries(
    Object.entries(variableValues).map(([variableName, variableValue]) => [
      variableName,
      variableValue === "#UA#" ? ua : typeof variableValue === "string" && variableValue.startsWith("#UA:") && variableValue.endsWith("#") ? getUserAgent(variableValue.substring(4, variableValue.length - 1)) : variableValue === "#UID#" ? uniqueId() : variableValue === "#REFERER#" ? referer : variableValue
    ])
  );
}

// src/getNextName.ts
function getNextName(existingNames, baseName) {
  let index = 1;
  function getName(i) {
    return `${baseName}${i > 1 ? i : ""}`;
  }
  while (existingNames[getName(index)]) {
    index += 1;
  }
  return getName(index);
}

// src/schema/getNextSchemaName.ts
function getNextSchemaName(schema, typeName) {
  return getNextName(
    Object.fromEntries(
      Object.keys(schema.objects).concat(Object.keys(schema.enums).concat(Object.keys(schema.unions))).map((name) => [name, true])
    ),
    typeName
  );
}
function getQueryCodeFromQuery({
  query,
  ...config
}) {
  if (query === null || query.fieldQuery === null || Object.keys(query.fieldQuery).length === 0) {
    return "query Full{root{*}}";
  }
  return `${config.includeComments ? `# To set up the SDK, follow the quickstart:
# https://docs.hypertune.com/quickstart

` : ""}query ${query.name ?? "FullQuery"}${Object.keys(query.variableDefinitions).length === 0 ? "" : `(${Object.entries(query.variableDefinitions).map(
    ([variableName, definition]) => (
      // TODO: default values
      `$${variableName}: ${getValueTypeCode(definition.valueType)}`
    )
  ).join(", ")})`}${getQueryCodeFromFieldQueryWithPadding(query.fragmentDefinitions, query.fieldQuery, query.variableDefinitions, config, 2)}
${!config.inlineSharedFragments ? `
${Object.entries(query.fragmentDefinitions).map(
    ([objectTypeName, fragment]) => `fragment ${objectTypeName} on ${objectTypeName} {
${getQueryCodeFromSelection(query.fragmentDefinitions, fragment.selection, query.variableDefinitions, config, 2)}}
`
  ).join("\n")}` : ""}`;
}
function getQueryCodeFromFieldQueryWithPadding(fragmentDefinitions, query, variableDefinitions, config, padOffset) {
  if (!query || Object.keys(query).length === 0) {
    return "";
  }
  const fragmentValues = Object.entries(query);
  if (fragmentValues.length > 1) {
    return ` {
${fragmentValues.map(([objectTypeName, fragment2]) => {
      return `${pad(padOffset)}... on ${objectTypeName} {
${!config.inlineSharedFragments && fragment2.type === "FragmentSpread" ? `${pad(padOffset + 2)}...${fragment2.fragmentName}
` : getQueryCodeFromSelection(
        fragmentDefinitions,
        getInlineFragment(fragmentDefinitions, fragment2).selection,
        variableDefinitions,
        config,
        padOffset + 2
      )}${pad(padOffset)}}`;
    }).join("\n")}
${pad(padOffset - 2)}}`;
  }
  const [, fragment] = fragmentValues[0];
  return ` {
${!config.inlineSharedFragments && fragment.type === "FragmentSpread" ? `${pad(padOffset)}...${fragment.fragmentName}
` : getQueryCodeFromSelection(
    fragmentDefinitions,
    getInlineFragment(fragmentDefinitions, fragment).selection,
    variableDefinitions,
    config,
    padOffset
  )}${pad(padOffset - 2)}}`;
}
function getQueryCodeFromSelection(fragmentDefinitions, selection, variableDefinitions, config, padOffset) {
  return `${Object.entries(selection).map(([fieldName, field]) => {
    return `${pad(padOffset)}${fieldName}${config.includeArguments ? getQueryCodeArgs(field.fieldArguments) : config.includeComments ? getQueryCodeArgsPrefix(field.fieldArguments, padOffset) : ""}${getQueryCodeFromFieldQueryWithPadding(
      fragmentDefinitions,
      field.fieldQuery,
      variableDefinitions,
      config,
      padOffset + 2
    )}`;
  }).join("\n")}
`;
}
function getQueryCodeArgsPrefix(fieldArguments, padOffset) {
  const args = getQueryCodeArgs(fieldArguments);
  if (!args) {
    return "";
  }
  return `
${pad(padOffset)}# Try uncommenting the line below and passing different args
${pad(padOffset)}# ${args}
${pad(padOffset - 1)}`;
}
function getQueryCodeArgs(fieldArguments) {
  if (!fieldArguments || Object.keys(fieldArguments).filter((key) => key !== isPartialObjectKey).length === 0) {
    return "";
  }
  return `(${Object.entries(fieldArguments).filter(([key]) => key !== isPartialObjectKey).map(([objectFieldName, value]) => {
    return `${objectFieldName}: ${stringifyQuery(value, " ")}`;
  }).join(", ")})`;
}
function pad(n) {
  return " ".repeat(n);
}
function stringifyQuery(value, padding) {
  if (Array.isArray(value)) {
    return `[${padding}${value.map((arrayValue) => stringifyQuery(arrayValue, padding)).join(`,${padding}`)}${padding}]`;
  }
  if (value instanceof Object) {
    if (isQueryVariableKey in value && value[isQueryVariableKey] && "name" in value && typeof value.name === "string") {
      return `$${value.name}`;
    }
    return `{${padding}${Object.entries(value).map(([fieldName, objectValue]) => {
      return `${fieldName}:${padding}${stringifyQuery(objectValue, padding)}`;
    }).join(`,${padding}`)}${padding}}`;
  }
  return JSON.stringify(value);
}
function getValueTypeCode(valueType) {
  switch (valueType.type) {
    case "BooleanValueType":
      return "Boolean!";
    case "StringValueType":
      return "String!";
    case "IntValueType":
      return "Int!";
    case "FloatValueType":
      return "Float!";
    case "EnumValueType":
      return `${valueType.enumTypeName}!`;
    case "ObjectValueType":
      return `${valueType.objectTypeName}!`;
    case "ListValueType":
      return `[${getValueTypeCode(valueType.itemValueType)}]!`;
    default:
      throw new Error(`unexpected value type: ${valueType}`);
  }
}

// src/getEpsilonGreedySplitConfig.ts
function getEpsilonGreedySplitConfig(split, armRewardMap) {
  const bestAssignment = {};
  Object.values(split.dimensions).forEach((dimension) => {
    if (dimension.type !== "discrete") {
      return;
    }
    const arms = Object.values(dimension.arms);
    if (arms.length === 0) {
      return;
    }
    const bestArmId = arms.map((arm) => {
      const armId = arm.id;
      const armReward = armRewardMap[armId];
      const expectedValue = armReward ? getExpectedValue(armReward) : 0;
      return { armId, expectedValue };
    }).reduce(
      (acc, curr) => curr.expectedValue > acc.expectedValue ? curr : acc
    ).armId;
    bestAssignment[dimension.id] = { type: "discrete", armId: bestArmId };
  });
  return {
    type: "EpsilonGreedyConfig",
    epsilon: 1,
    // TODO: Make smarter
    bestAssignment
  };
}
function getExpectedValue(armReward) {
  const { exposure_count, reward_sum } = armReward;
  return exposure_count > 0 ? reward_sum / exposure_count : 0;
}
function getInitDataResponse(commitData, reducedExpression, skipLegacyData) {
  const { splits, commitConfig } = getSplitsAndCommitConfigForExpression(
    reducedExpression,
    commitData.splits,
    commitData.config
  );
  const initData = {
    commitId: commitData.id,
    hash: commitData.hash,
    reducedExpression,
    splits,
    commitConfig
  };
  if (skipLegacyData) {
    return initData;
  }
  const legacyInitData = {
    ...initData,
    commitHash: commitData.hash,
    eventTypes: commitData.eventTypes,
    initLogId: 0,
    sdkConfig: {
      hashPollInterval: 1e3,
      flushLogsInterval: 1e3,
      maxLogsPerFlush: 1
    }
  };
  return legacyInitData;
}

// src/getWebEnvironment.ts
function getWebEnvironment() {
  return getWebEnvironmentInternal(
    typeof window === "undefined" ? void 0 : window?.location?.hostname,
    process.env.NODE_ENV
  );
}
function getWebEnvironmentInternal(hostname, nodeEnv) {
  if (hostname === void 0) {
    return nodeEnv === "development" ? "local" : "prod";
  }
  if (hostname.endsWith("prod.hypertune.com") || // Regex matches *.hypertune.com, where * does not include a dot
  /^([^.]+\.)?hypertune\.com$/.test(hostname)) {
    return "prod";
  }
  if (hostname.endsWith("staging.hypertune.com")) {
    return "staging";
  }
  return "local";
}

// src/deepClonePlainObject.ts
function deepClonePlainObject(object) {
  return JSON.parse(JSON.stringify(object));
}
function deepEqualsPlainObject(a, b) {
  return stableStringify(a) === stableStringify(b);
}

// src/isValidEmail.ts
function isValidEmail(email) {
  return !!email.toLowerCase().match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
}

// src/isValidDomain.ts
function isValidDomain(domain) {
  return !!domain.toLowerCase().match(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/
  );
}

// src/isValidUrl.ts
function isValidUrl(url) {
  try {
    const parsedUrl = new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

// src/schema/isPrimitiveValueType.ts
function isPrimitiveValueType(valueType) {
  return valueType.type === "BooleanValueType" || valueType.type === "IntValueType" || valueType.type === "FloatValueType" || valueType.type === "StringValueType" || valueType.type === "RegexValueType" || valueType.type === "EnumValueType";
}

// src/sanitizeBranchName.ts
function sanitizeBranchName(branchName) {
  return branchName.replaceAll(".", "_").replaceAll("/", "_");
}

// src/getVercelEdgeConfigItemKey.ts
function getVercelEdgeConfigItemKey(projectId, branchName) {
  if (branchName === defaultBranchName) {
    return `hypertune_${projectId}`;
  }
  return `hypertune_${projectId}_${sanitizeBranchName(branchName)}`;
}

// src/schema/getDefaultQueryCode.ts
function getDefaultQueryCode({
  schema,
  includeDeprecated,
  includeComments,
  includeArguments,
  useSharedFragments
}) {
  const query = getDefaultQuery({
    schema,
    includeDeprecated,
    useSharedFragments,
    includeArguments: includeArguments || includeComments,
    markQueryFieldArgumentsPartial: false,
    objectTypeNames: [queryObjectTypeName]
  });
  return query ? getQueryCodeFromQuery({
    query,
    includeComments,
    includeArguments,
    inlineSharedFragments: !useSharedFragments
  }) : null;
}

// src/schema/getAllValuePathsForObject.ts
function getAllValuePathsForObject(schema, objectTypeName, includeLeafValueTypes) {
  return Object.entries(schema.objects[objectTypeName]?.fields ?? {}).flatMap(
    ([fieldName, fieldSchema]) => {
      switch (fieldSchema.valueType.type) {
        case "BooleanValueType":
        case "StringValueType":
        case "IntValueType":
        case "FloatValueType":
        case "EnumValueType":
          if (includeLeafValueTypes && !includeLeafValueTypes(fieldSchema.valueType)) {
            return [];
          }
          return [[fieldName]];
        case "ObjectValueType":
          return getAllValuePathsForObject(
            schema,
            fieldSchema.valueType.objectTypeName,
            includeLeafValueTypes
          ).map((path) => [fieldName, ...path]);
        default:
          return [];
      }
    }
  );
}

// src/schema/getStringValuePathsForObject.ts
function getStringValuePathsForObject(schema, objectTypeName) {
  return Object.entries(schema.objects[objectTypeName]?.fields ?? {}).flatMap(
    ([fieldName, fieldSchema]) => {
      if (fieldSchema.valueType.type === "StringValueType") {
        return [[fieldName]];
      }
      if (fieldSchema.valueType.type === "ObjectValueType") {
        return getStringValuePathsForObject(
          schema,
          fieldSchema.valueType.objectTypeName
        ).map((path) => [fieldName, ...path]);
      }
      return [];
    }
  );
}

// src/schema/isSchemaNameValid.ts
var startsWithLetterRegex = /^[a-zA-Z].*$/;
var noSpecialCharactersRegex = /^[a-zA-Z0-9_]+$/;
function isSchemaNameValid(name) {
  return checkSchemaName(name).valid;
}
function checkSchemaName(name) {
  const startsWithLetter = startsWithLetterRegex.test(name);
  const noSpecialCharacters = noSpecialCharactersRegex.test(name);
  return {
    startsWithLetter,
    noSpecialCharacters,
    valid: startsWithLetter && noSpecialCharacters
  };
}
function schemaNameErrorMessage(typeName, name) {
  return `${typeName} name "${name}" is not valid. Please don't use special characters and make sure the name starts with a letter.`;
}
function alreadyExistsMessage(typeName, name) {
  return `${typeName} with name "${name}" already exists. Please use a different name.`;
}

// src/schema/valueTypeOperations.ts
function renameObjectInValueType(valueType, oldObjectTypeName, newObjectTypeName) {
  return mapValueType(valueType, (currentValueType) => {
    if (currentValueType.type === "FunctionValueType" && currentValueType.parameterValueTypes.length === 1 && currentValueType.parameterValueTypes[0].type === "ObjectValueType") {
      const argsObjectFields = getFieldArgumentsObjectTypeNameParts(
        currentValueType.parameterValueTypes[0].objectTypeName
      );
      if (argsObjectFields !== null && argsObjectFields.parentObjectTypeName === oldObjectTypeName) {
        return {
          ...currentValueType,
          parameterValueTypes: [
            {
              ...currentValueType.parameterValueTypes[0],
              objectTypeName: getFieldArgumentsObjectTypeName({
                parentObjectTypeName: newObjectTypeName,
                fieldName: argsObjectFields.fieldName
              })
            }
          ]
        };
      }
      return currentValueType;
    }
    if (currentValueType.type === "ObjectValueType" && currentValueType.objectTypeName === oldObjectTypeName) {
      return {
        ...currentValueType,
        objectTypeName: newObjectTypeName
      };
    }
    return currentValueType;
  });
}
function renameEnumInValueType(valueType, oldEnumTypeName, newEnumTypeName) {
  return mapValueType(valueType, (currentValueType) => {
    if (currentValueType.type === "EnumValueType" && currentValueType.enumTypeName === oldEnumTypeName) {
      return {
        ...currentValueType,
        enumTypeName: newEnumTypeName
      };
    }
    return currentValueType;
  });
}
function mapValueType(valueType, mapFunction) {
  switch (valueType.type) {
    case "FunctionValueType": {
      return mapFunction({
        ...valueType,
        parameterValueTypes: valueType.parameterValueTypes.map(
          (parameterValueType) => {
            return mapValueType(parameterValueType, mapFunction);
          }
        ),
        returnValueType: mapValueType(valueType.returnValueType, mapFunction)
      });
    }
    case "ListValueType":
      return mapFunction({
        ...valueType,
        itemValueType: mapValueType(valueType.itemValueType, mapFunction)
      });
    default:
      return mapFunction(valueType);
  }
}

// src/schema/schemaOperationsObject.ts
function addEmptyObject(schema, objectTypeName, role, description) {
  if (!isSchemaNameValid(objectTypeName)) {
    throw new Error(schemaNameErrorMessage("Object", objectTypeName));
  }
  if (schema.objects[objectTypeName] || schema.unions[objectTypeName] || schema.enums[objectTypeName]) {
    throw new Error(alreadyExistsMessage("Type", objectTypeName));
  }
  return {
    ...schema,
    objects: {
      ...schema.objects,
      [objectTypeName]: {
        description,
        role,
        fields: {}
      }
    }
  };
}
function addDefaultEvent(schema, objectTypeName, description) {
  if (!isSchemaNameValid(objectTypeName)) {
    throw new Error(schemaNameErrorMessage("Object", objectTypeName));
  }
  if (schema.objects[objectTypeName] || schema.unions[objectTypeName] || schema.enums[objectTypeName]) {
    throw new Error(alreadyExistsMessage("Type", objectTypeName));
  }
  const contextObjectTypeName = rootContextTypeNameFromSchema(schema);
  const newSchema = {
    ...schema,
    objects: {
      ...schema.objects,
      [objectTypeName]: {
        description,
        role: "event",
        fields: {}
      }
    }
  };
  return contextObjectTypeName ? addFieldToBasicObject(
    newSchema,
    objectTypeName,
    "context",
    { type: "ObjectValueType", objectTypeName: contextObjectTypeName },
    null,
    "first"
  ) : newSchema;
}
function renameObject(schema, oldObjectTypeName, rawNewObjectTypeName) {
  const newObjectTypeName = formatTypeSchemaName(rawNewObjectTypeName);
  if (oldObjectTypeName === newObjectTypeName) {
    return schema;
  }
  if (!isSchemaNameValid(newObjectTypeName)) {
    throw new Error(schemaNameErrorMessage("Object", newObjectTypeName));
  }
  if (!schema.objects[oldObjectTypeName]) {
    throw new Error(`oldObjectTypeName does not exist: ${oldObjectTypeName}`);
  }
  if (schema.objects[newObjectTypeName] || schema.unions[newObjectTypeName] || schema.enums[newObjectTypeName]) {
    throw new Error(alreadyExistsMessage("Type", newObjectTypeName));
  }
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(([objectName, objectSchema]) => {
        const newObjectSchema = {
          ...objectSchema,
          fields: Object.fromEntries(
            Object.entries(objectSchema.fields).map(
              ([fieldName, fieldSchema]) => [
                fieldName,
                {
                  ...fieldSchema,
                  valueType: renameObjectInValueType(
                    fieldSchema.valueType,
                    oldObjectTypeName,
                    newObjectTypeName
                  )
                }
              ]
            )
          )
        };
        if (objectName === oldObjectTypeName) {
          return [newObjectTypeName, newObjectSchema];
        }
        const argsObjectParts = getFieldArgumentsObjectTypeNameParts(objectName);
        if (argsObjectParts !== null && argsObjectParts.parentObjectTypeName === oldObjectTypeName) {
          return [
            getFieldArgumentsObjectTypeName({
              parentObjectTypeName: newObjectTypeName,
              fieldName: argsObjectParts.fieldName
            }),
            newObjectSchema
          ];
        }
        return [objectName, newObjectSchema];
      })
    ),
    unions: Object.fromEntries(
      Object.entries(schema.unions).map(([unionName, unionSchema]) => [
        unionName,
        {
          ...unionSchema,
          variants: Object.fromEntries(
            Object.entries(unionSchema.variants).map(
              ([variantName, variantValue]) => [
                variantName === oldObjectTypeName ? newObjectTypeName : variantName,
                variantValue
              ]
            )
          )
        }
      ])
    )
  };
}
function changeObjectRole(schema, objectTypeName, newRole) {
  const tempObjectTypeName = getNextSchemaName(schema, objectTypeName);
  return renameObject(
    removeObject(
      cloneObject(schema, objectTypeName, tempObjectTypeName, newRole),
      objectTypeName
    ).schema,
    tempObjectTypeName,
    objectTypeName
  );
}
function removeObject(schema, objectTypeName) {
  if (!schema.objects[objectTypeName]) {
    throw new Error(`objectTypeName does not exist: ${objectTypeName}`);
  }
  Object.entries(schema.unions).forEach(([unionName, unionSchema]) => {
    if (unionSchema.variants[objectTypeName] && Object.keys(unionSchema.variants).length === 1) {
      throw new Error(
        `Object "${objectTypeName}" is the only variant of a union "${unionName}" add more variants or remove the union first`
      );
    }
  });
  const fieldsToRemove = {};
  Object.entries(schema.objects).filter(([objectName]) => objectName !== objectTypeName).forEach(([objectName, objectType]) => {
    const objectFields = Object.keys(objectType.fields);
    const objectFieldsToRemove = objectFields.filter(
      (fieldName) => valueTypeReturnsObject(
        objectType.fields[fieldName].valueType,
        objectTypeName
      )
    );
    if (getFieldArgumentsObjectTypeNameParts(objectName) === null && objectFieldsToRemove.length > 0 && objectFields.length - objectFieldsToRemove.length === 0) {
      throw new Error(
        `Removing object "${objectTypeName}" would result in object "${objectName}" being empty. Delete it or add other fields to it first.`
      );
    }
    if (objectFieldsToRemove.length > 0) {
      fieldsToRemove[objectName] = objectFieldsToRemove;
    }
  });
  fieldsToRemove[objectTypeName] = Object.keys(
    schema.objects[objectTypeName].fields
  );
  return {
    schema: {
      ...schema,
      objects: Object.fromEntries(
        Object.entries(
          removeFieldsFromObjects(schema, fieldsToRemove).objects
        ).filter(([objectName]) => objectName !== objectTypeName)
      ),
      unions: Object.fromEntries(
        Object.entries(schema.unions).map(([unionName, unionSchema]) => [
          unionName,
          {
            ...unionSchema,
            variants: Object.fromEntries(
              Object.entries(unionSchema.variants).filter(
                ([variantName]) => variantName !== objectTypeName
              )
            )
          }
        ])
      )
    },
    removedFields: fieldsToRemove
  };
}
function cloneObject(schema, sourceObjectTypeName2, newObjectTypeName, newObjectRole) {
  if (!schema.objects[sourceObjectTypeName2]) {
    throw new Error(
      `sourceObjectTypeName does not exist: ${sourceObjectTypeName2}`
    );
  }
  if (schema.objects[sourceObjectTypeName2].role === "args") {
    throw new Error(
      `can't clone args objects explicitly: ${sourceObjectTypeName2}`
    );
  }
  return Object.entries(
    schema.objects[sourceObjectTypeName2].fields
  ).reduce(
    (newSchema, [fieldName, fieldSchema]) => {
      const valueType = fieldSchema.valueType.type === "FunctionValueType" ? fieldSchema.valueType.returnValueType : fieldSchema.valueType;
      return addFieldToObject(
        newSchema,
        newObjectTypeName,
        fieldName,
        valueType,
        fieldSchema.description,
        "last"
      );
    },
    addEmptyObject(schema, newObjectTypeName, newObjectRole, null)
  );
}
function setObjectDescription(schema, objectTypeName, description) {
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(([objectName, objectSchema]) => [
        objectName,
        objectName === objectTypeName ? { ...objectSchema, description } : objectSchema
      ])
    )
  };
}
function setObjectFieldDescription(schema, objectTypeName, fieldName, description) {
  return setObjectFieldSchema(
    schema,
    objectTypeName,
    fieldName,
    (fieldSchema) => {
      return {
        ...fieldSchema,
        description
      };
    }
  );
}
function setObjectFieldDeprecationReason(schema, objectTypeName, fieldName, deprecationReason) {
  return setObjectFieldSchema(
    schema,
    objectTypeName,
    fieldName,
    (fieldSchema) => {
      const { deprecationReason: _, ...baseFieldSchema } = fieldSchema;
      return {
        ...baseFieldSchema,
        ...deprecationReason === void 0 ? {} : { deprecationReason }
      };
    }
  );
}
function addFieldToObject(schema, objectTypeName, rawNewFieldName, valueType, description, position = "first") {
  const newFieldName = formatFieldSchemaName(rawNewFieldName);
  if (!isSchemaNameValid(newFieldName)) {
    throw new Error(schemaNameErrorMessage("Field", newFieldName));
  }
  if (!schema.objects[objectTypeName]) {
    throw new Error(`objectTypeName doesn't exist: ${objectTypeName}`);
  }
  if (schema.objects[objectTypeName].fields[newFieldName]) {
    throw new Error(alreadyExistsMessage("Field", newFieldName));
  }
  if (schema.objects[objectTypeName].role === "output") {
    return addFieldToOutputObject(
      schema,
      objectTypeName,
      newFieldName,
      valueType,
      description,
      position
    );
  }
  return addFieldToBasicObject(
    schema,
    objectTypeName,
    newFieldName,
    valueType,
    description,
    position
  );
}
function addFieldToOutputObject(schema, parentObjectTypeName, fieldName, valueType, description, position) {
  const argsObjectName = getFieldArgumentsObjectTypeName({
    parentObjectTypeName,
    fieldName
  });
  return addFieldToBasicObject(
    // Add args object to schema before adding the field in.
    {
      ...schema,
      objects: {
        ...schema.objects,
        [argsObjectName]: {
          description: null,
          role: "args",
          fields: {}
        }
      }
    },
    parentObjectTypeName,
    fieldName,
    {
      type: "FunctionValueType",
      parameterValueTypes: [
        {
          type: "ObjectValueType",
          objectTypeName: argsObjectName
        }
      ],
      returnValueType: valueType
    },
    description,
    position
  );
}
function addFieldToBasicObject(schema, parentObjectTypeName, fieldName, valueType, description, position) {
  const newField = { [fieldName]: { description, valueType } };
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(([objectTypeName, objectType]) => [
        objectTypeName,
        objectTypeName === parentObjectTypeName ? {
          ...objectType,
          fields: {
            ...position === "first" ? newField : {},
            ...objectType.fields,
            ...position === "last" ? newField : {}
          }
        } : objectType
      ])
    )
  };
}
function renameObjectField(schema, objectTypeName, oldFieldName, rawNewFieldName) {
  const newFieldName = formatFieldSchemaName(rawNewFieldName);
  if (oldFieldName === newFieldName) {
    return schema;
  }
  if (!isSchemaNameValid(newFieldName)) {
    throw new Error(schemaNameErrorMessage("Field", newFieldName));
  }
  if (!schema.objects[objectTypeName]) {
    throw new Error(`objectTypeName does not exist: ${objectTypeName}`);
  }
  if (!schema.objects[objectTypeName].fields[oldFieldName]) {
    throw new Error(`fieldName does not exist: ${oldFieldName}`);
  }
  if (schema.objects[objectTypeName].fields[newFieldName]) {
    throw new Error(alreadyExistsMessage("Field", newFieldName));
  }
  const oldArgsObjectName = argsNameFromValueType(
    schema.objects[objectTypeName].fields[oldFieldName].valueType
  );
  const newArgsObjectName = getFieldArgumentsObjectTypeName({
    parentObjectTypeName: objectTypeName,
    fieldName: newFieldName
  });
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(
        ([objectName, objectType]) => objectName === oldArgsObjectName ? [newArgsObjectName, objectType] : objectName !== objectTypeName ? [objectName, objectType] : [
          objectName,
          {
            ...objectType,
            fields: Object.fromEntries(
              Object.entries(objectType.fields).map(
                ([fieldName, fieldSchema]) => [
                  oldFieldName === fieldName ? newFieldName : fieldName,
                  oldFieldName === fieldName && oldArgsObjectName ? {
                    ...fieldSchema,
                    valueType: renameObjectInValueType(
                      fieldSchema.valueType,
                      oldArgsObjectName,
                      newArgsObjectName
                    )
                  } : fieldSchema
                ]
              )
            )
          }
        ]
      )
    )
  };
}
function changeObjectFieldType(schema, objectTypeName, fieldName, newValueType) {
  return setObjectFieldSchema(
    schema,
    objectTypeName,
    fieldName,
    (fieldSchema) => {
      return {
        ...fieldSchema,
        valueType: fieldSchema.valueType.type === "FunctionValueType" ? {
          ...fieldSchema.valueType,
          returnValueType: newValueType
        } : newValueType
      };
    }
  );
}
function setObjectFieldSchema(schema, objectTypeName, fieldName, newFieldSchema) {
  if (!schema.objects[objectTypeName]) {
    throw new Error(`objectTypeName does not exist: ${objectTypeName}`);
  }
  if (!schema.objects[objectTypeName].fields[fieldName]) {
    throw new Error(`fieldName does not exist: ${fieldName}`);
  }
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(
        ([objectName, objectType]) => objectName !== objectTypeName ? [objectName, objectType] : [
          objectName,
          {
            ...objectType,
            fields: Object.fromEntries(
              Object.entries(objectType.fields).map(
                ([objectFieldName, fieldSchema]) => [
                  objectFieldName,
                  objectFieldName === fieldName ? newFieldSchema(fieldSchema) : fieldSchema
                ]
              )
            )
          }
        ]
      )
    )
  };
}
function removeFieldFromObject(schema, objectTypeName, fieldName) {
  if (!schema.objects[objectTypeName]) {
    throw new Error(`objectTypeName does not exist: ${objectTypeName}`);
  }
  if (!schema.objects[objectTypeName].fields[fieldName]) {
    throw new Error(`fieldName does not exist: ${fieldName}`);
  }
  return removeFieldsFromObjects(schema, { [objectTypeName]: [fieldName] });
}
function removeFieldsFromObjects(schema, fieldsToRemove) {
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).filter(([objectName]) => {
        const argsObjectParts = getFieldArgumentsObjectTypeNameParts(objectName);
        if (!argsObjectParts) {
          return true;
        }
        if (!fieldsToRemove[argsObjectParts.parentObjectTypeName]) {
          return true;
        }
        return (
          // Check if this field is being removed. If it its index in the array won't be -1.
          fieldsToRemove[argsObjectParts.parentObjectTypeName].indexOf(
            argsObjectParts.fieldName
          ) === -1
        );
      }).map(([objectName, objectSchema]) => [
        objectName,
        !fieldsToRemove[objectName] ? objectSchema : {
          ...objectSchema,
          fields: Object.fromEntries(
            Object.entries(objectSchema.fields).filter(
              ([fieldName]) => fieldsToRemove[objectName].indexOf(fieldName) === -1
            )
          )
        }
      ])
    )
  };
}
function rootObjectTypeNameFromSchema(schema) {
  return schema.objects[queryObjectTypeName].fields[rootFieldName].valueType.returnValueType.objectTypeName;
}
function rootContextTypeNameFromSchema(schema) {
  const argsObjectFields = Object.entries(
    schema.objects[schema.objects[queryObjectTypeName].fields[rootFieldName].valueType.parameterValueTypes[0].objectTypeName].fields
  );
  if (argsObjectFields.length === 0) {
    return null;
  }
  const [, fieldSchema] = argsObjectFields[0];
  if (fieldSchema.valueType.type !== "ObjectValueType") {
    return null;
  }
  return fieldSchema.valueType.objectTypeName;
}
function formatTypeSchemaName(name) {
  return toStartCaseWords(name).join("");
}
function formatFieldSchemaName(name) {
  return toStartCaseWords(name).map((word, index) => index === 0 ? word.toLowerCase() : word).join("");
}
function argsNameFromValueType(valueType) {
  return valueType.type === "FunctionValueType" && valueType.parameterValueTypes.length === 1 && valueType.parameterValueTypes[0].type === "ObjectValueType" ? valueType.parameterValueTypes[0].objectTypeName : null;
}
function valueTypeReturnsObject(valueType, objectTypeName) {
  switch (valueType.type) {
    case "ObjectValueType":
      return valueType.objectTypeName === objectTypeName;
    case "FunctionValueType":
      return valueTypeReturnsObject(valueType.returnValueType, objectTypeName);
    case "ListValueType":
      return valueTypeReturnsObject(valueType.itemValueType, objectTypeName);
    default:
      return false;
  }
}

// src/schema/filterAndSortObjectEntries.ts
function filterAndSortObjectEntries(schema) {
  const contextObjectTypeName = rootContextTypeNameFromSchema(schema);
  const rootObjectTypeName2 = rootObjectTypeNameFromSchema(schema);
  return Object.entries(schema.objects).filter(
    ([objectTypeName, objectType]) => !objectTypeName.endsWith(argsObjectSuffix) && objectType.role !== "args"
  ).sort(([objectTypeNameA, objectTypeA], [objectTypeNameB, objectTypeB]) => {
    if (objectTypeNameA === queryObjectTypeName) {
      return -1;
    }
    if (objectTypeNameB === queryObjectTypeName) {
      return 1;
    }
    if (objectTypeNameA === contextObjectTypeName) {
      return -1;
    }
    if (objectTypeNameB === contextObjectTypeName) {
      return 1;
    }
    return compareObjectRolesAndNames(
      rootObjectTypeName2,
      objectTypeA.role,
      objectTypeNameA,
      objectTypeB.role,
      objectTypeNameB
    );
  });
}
function compareObjectRolesAndNames(rootObjectTypeName2, objectRoleA, objectTypeNameA, objectRoleB, objectTypeNameB) {
  if (objectRoleA === "input" && objectRoleB !== "input") {
    return -1;
  }
  if (objectRoleB === "input" && objectRoleA !== "input") {
    return 1;
  }
  if (objectTypeNameA === rootObjectTypeName2) {
    return -1;
  }
  if (objectTypeNameB === rootObjectTypeName2) {
    return 1;
  }
  if (objectRoleA === "event" && objectRoleB !== "event") {
    return 1;
  }
  if (objectRoleB === "event" && objectRoleA !== "event") {
    return -1;
  }
  return objectTypeNameA.localeCompare(objectTypeNameB);
}

// src/schema/getSchemaCodeFromSchema.ts
function getSchemaCodeFromSchema(schema, filter) {
  const hasEnums = Object.keys(schema.enums).length > 0;
  const hasUnions = Object.keys(schema.unions).length > 0;
  const hasTags = schema.tags && Object.keys(schema.tags).length > 0;
  return `${objectsToCode(schema, filter)}
${hasUnions || hasEnums ? "\n" : ""}${unionsToCode(schema, filter)}${hasUnions ? "\n\n" : ""}${enumsToCode(schema, filter)}${hasEnums ? "\n" : ""}${hasTags ? "\n" : ""}${tagsToCode(schema)}`;
}
function objectsToCode(schema, filter) {
  return filterAndSortObjectEntries(schema).filter(([objectTypeName]) => includeTypeName(filter, objectTypeName)).map(([objectTypeName]) => {
    return objectToCode(schema, objectTypeName);
  }).join("\n\n");
}
function objectToCode(schema, objectTypeName, omitDescriptions) {
  const objectSchema = schema.objects[objectTypeName];
  return `${descriptionToCode(objectSchema.description, "", omitDescriptions)}${objectSchema.role === "output" ? "type" : "input"} ${objectTypeName}${objectSchema.role === "event" ? " @event" : ""}${Object.keys(objectSchema.fields).length > 0 ? ` {
  ${Object.keys(objectSchema.fields).map(
    (fieldName) => objectFieldToCode(schema, objectTypeName, fieldName, omitDescriptions)
  ).join("\n  ")}
}` : ""}`;
}
function tagsToCode(schema) {
  if (!schema.tags || Object.keys(schema.tags).length === 0) {
    return "";
  }
  return `type ${internalTypeName} @data(
  tags: [
${Object.values(schema.tags)?.map((tag) => `    { name: "${tag.name}", color: "${tag.color}" },`).join(`
`) ?? ""}
  ]
)
`;
}
function objectFieldToCode(schema, objectTypeName, fieldName, omitDescriptions) {
  const fieldSchema = schema.objects[objectTypeName].fields[fieldName];
  return `${descriptionToCode(
    fieldSchema.description,
    "  ",
    omitDescriptions
  )}${fieldName}${argsToCode(
    schema,
    fieldSchema.valueType
  )}: ${fieldTypeToGraphqlFieldType(
    fieldSchema.valueType
  )}${deprecationReasonToCode(
    fieldSchema.deprecationReason,
    omitDescriptions
  )}`;
}
function argsToCode(schema, fieldType) {
  if (fieldType.type !== "FunctionValueType" || fieldType.parameterValueTypes.length !== 1 || fieldType.parameterValueTypes[0].type !== "ObjectValueType" || !schema.objects[fieldType.parameterValueTypes[0].objectTypeName] || Object.keys(
    schema.objects[fieldType.parameterValueTypes[0].objectTypeName].fields
  ).length === 0) {
    return "";
  }
  return `(${Object.entries(
    schema.objects[fieldType.parameterValueTypes[0].objectTypeName].fields
  ).map(
    ([argName, argField]) => `${argName}: ${fieldTypeToGraphqlFieldType(argField.valueType)}`
  ).join(", ")})`;
}
function fieldTypeToGraphqlFieldType(fieldType) {
  switch (fieldType.type) {
    case "VoidValueType":
      return "Void!";
    case "BooleanValueType":
      return "Boolean!";
    case "IntValueType":
      return "Int!";
    case "FloatValueType":
      return "Float!";
    case "StringValueType":
      return "String!";
    case "EnumValueType":
      return `${fieldType.enumTypeName}!`;
    case "FunctionValueType":
      return fieldTypeToGraphqlFieldType(fieldType.returnValueType);
    case "ObjectValueType":
      return `${fieldType.objectTypeName}!`;
    case "ListValueType":
      return `[${fieldTypeToGraphqlFieldType(fieldType.itemValueType)}]!`;
    case "UnionValueType":
      return `${fieldType.unionTypeName}!`;
    default:
      throw Error(`unexpected object field type: ${fieldType.type}`);
  }
}
function descriptionToCode(description, padding, omit) {
  if (description === null || omit) {
    return "";
  }
  return `"""
${description.split("\n").map((line) => `${padding}${line}`).join("\n")}
${padding}"""
${padding}`;
}
function deprecationReasonToCode(deprecationReason, omit) {
  if (omit) {
    return "";
  }
  return deprecationReason !== void 0 ? ` @deprecated(reason: "${deprecationReason}")` : "";
}
function unionsToCode(schema, filter) {
  return Object.keys(schema.unions).filter((unionName) => includeTypeName(filter, unionName)).sort((unionNameA, unionNameB) => unionNameA.localeCompare(unionNameB)).map((unionName) => unionToCode(schema, unionName)).filter(Boolean).join("\n\n");
}
function unionToCode(schema, unionName, omitDescriptions) {
  const unionSchema = schema.unions[unionName];
  if (Object.keys(unionSchema.variants).length === 0) {
    return "";
  }
  return `${descriptionToCode(
    unionSchema.description,
    "",
    omitDescriptions
  )}union ${unionName} = ${Object.keys(unionSchema.variants).join(" | ")}`;
}
function enumsToCode(schema, filter) {
  return Object.keys(schema.enums).filter((enumName) => includeTypeName(filter, enumName)).sort((enumNameA, enumNameB) => enumNameA.localeCompare(enumNameB)).map((enumName) => {
    return enumToCode(schema, enumName);
  }).filter(Boolean).join("\n\n");
}
function enumToCode(schema, enumName, omitDescriptions) {
  const enumSchema = schema.enums[enumName];
  if (Object.keys(enumSchema.values).length === 0) {
    return "";
  }
  return `${descriptionToCode(
    enumSchema.description,
    "",
    omitDescriptions
  )}enum ${enumName} {
${Object.entries(enumSchema.values).map(
    ([valueName, value]) => `  ${descriptionToCode(
      value.description,
      "  ",
      omitDescriptions
    )}${valueName}${deprecationReasonToCode(value.deprecationReason)}`
  ).join(",\n")}
}`;
}
function includeTypeName(filter, typeName) {
  return !filter || filter.type === "include" && filter.values.has(typeName) || filter.type === "exclude" && !filter.values.has(typeName);
}

// src/schema/getObjectTypeSchemaReferences.ts
function getObjectTypeSchemaReferences(schema, objectTypeName) {
  const objectSchema = schema.objects[objectTypeName];
  if (!objectSchema) {
    throw new Error(`invalid objectTypeName: ${objectTypeName}`);
  }
  if (objectSchema.role !== "output") {
    throw new Error(
      `unsupported object type ${schema.objects[objectTypeName].role} for object ${objectTypeName}`
    );
  }
  return getOutputObjectTypeSchemaReferencesFromValueType(
    schema,
    objectTypeName,
    {
      type: "ObjectValueType",
      objectTypeName: rootObjectTypeNameFromSchema(schema)
    },
    /* @__PURE__ */ new Set(),
    {
      objectTypeName: queryObjectTypeName,
      fieldName: rootFieldName,
      isList: false
    }
  );
}
function getOutputObjectTypeSchemaReferencesFromValueType(schema, objectTypeName, valueType, visited, reference) {
  switch (valueType.type) {
    case "FunctionValueType":
      return getOutputObjectTypeSchemaReferencesFromValueType(
        schema,
        objectTypeName,
        valueType.returnValueType,
        visited,
        reference
      );
    case "ListValueType":
      return getOutputObjectTypeSchemaReferencesFromValueType(
        schema,
        objectTypeName,
        valueType.itemValueType,
        visited,
        { ...reference, isList: true }
      );
    case "ObjectValueType": {
      const result = valueType.objectTypeName === objectTypeName ? [reference] : [];
      if (visited.has(valueType.objectTypeName)) {
        return result;
      }
      visited.add(valueType.objectTypeName);
      return Object.entries(schema.objects[valueType.objectTypeName].fields).flatMap(
        ([fieldName, field]) => getOutputObjectTypeSchemaReferencesFromValueType(
          schema,
          objectTypeName,
          field.valueType,
          visited,
          {
            fieldName,
            objectTypeName: valueType.objectTypeName,
            isList: false
          }
        )
      ).concat(result);
    }
    default:
      return [];
  }
}

// src/schema/formatSchemaCode.ts
function formatSchemaCode(schemaCode, typeFilter) {
  return getSchemaCodeFromSchema(
    getSchemaWithDescriptions(schemaCode),
    typeFilter
  );
}
function getSchemaWithDescriptions(schemaCode) {
  const codeWithNewMessage = schemaCode.replace(oldSchemaWelcomeMessage, newSchemaWelcomeMessage).replace(oldSchemaRootComment, newSchemaRootComment);
  let graphqlSchema;
  try {
    graphqlSchema = getGraphqlSchema(
      convertCommentsToDescriptions(codeWithNewMessage)
    );
  } catch (error) {
    graphqlSchema = getGraphqlSchema(codeWithNewMessage);
  }
  return getSchema(graphqlSchema);
}
function convertCommentsToDescriptions(code) {
  return code.split("\n").map((line) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith("#")) {
      return `"""
${trimmedLine.slice(1).trim()}
"""`;
    }
    const parts = trimmedLine.split("#", 2);
    if (parts.length === 2) {
      return `"""
${parts[1].trim()}
"""
${parts[0]}`;
    }
    return line;
  }).join("\n").split(`"""
"""
`).join("");
}

// src/stringifyWithoutQuotes.ts
function stringifyWithoutQuotes(value, padding = "") {
  if (Array.isArray(value)) {
    return `[${padding}${value.map((arrayValue) => stringifyWithoutQuotes(arrayValue, padding)).join(`,${padding}`)}${padding}]`;
  }
  if (value instanceof Object) {
    return `{${padding}${Object.entries(value).map(([fieldName, objectValue]) => {
      return `${fieldName}:${padding}${stringifyWithoutQuotes(
        objectValue,
        padding
      )}`;
    }).join(`,${padding}`)}${padding}}`;
  }
  return JSON.stringify(value);
}

// src/toCamelCase.ts
function toCamelCase(text) {
  const result = toCapitalizedCamelCase(text);
  return result.substring(0, 1).toLowerCase() + result.substring(1);
}
function toCapitalizedCamelCase(text) {
  return text.replace(/[-_\s.]+(.)?/g, (_, c) => c ? c.toUpperCase() : "");
}

// src/schema/schemaOperationsEnum.ts
function addEmptyEnum(schema, enumTypeName, description) {
  if (!isSchemaNameValid(enumTypeName)) {
    throw new Error(schemaNameErrorMessage("Enum", enumTypeName));
  }
  if (schema.objects[enumTypeName] || schema.unions[enumTypeName] || schema.enums[enumTypeName]) {
    throw new Error(alreadyExistsMessage("Type", enumTypeName));
  }
  return {
    ...schema,
    enums: {
      ...schema.enums,
      [enumTypeName]: {
        description,
        values: {
          [enumTypeName + 1]: { description: null }
        }
      }
    }
  };
}
function renameEnum(schema, oldEnumTypeName, rawNewEnumTypeName) {
  const newEnumTypeName = formatTypeSchemaName(rawNewEnumTypeName);
  if (oldEnumTypeName === newEnumTypeName) {
    return schema;
  }
  if (!isSchemaNameValid(newEnumTypeName)) {
    throw new Error(schemaNameErrorMessage("Enum", newEnumTypeName));
  }
  if (!schema.enums[oldEnumTypeName]) {
    throw new Error(`oldEnumTypeName does not exist: ${oldEnumTypeName}`);
  }
  if (schema.objects[newEnumTypeName] || schema.unions[newEnumTypeName] || schema.enums[newEnumTypeName]) {
    throw new Error(alreadyExistsMessage("Type", newEnumTypeName));
  }
  return {
    ...schema,
    enums: Object.fromEntries(
      Object.entries(schema.enums).map(([enumName, enumSchema]) => [
        enumName === oldEnumTypeName ? newEnumTypeName : enumName,
        enumSchema
      ])
    ),
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(([objectName, objectSchema]) => [
        objectName,
        {
          ...objectSchema,
          fields: Object.fromEntries(
            Object.entries(objectSchema.fields).map(
              ([fieldName, fieldSchema]) => [
                fieldName,
                {
                  ...fieldSchema,
                  valueType: renameEnumInValueType(
                    fieldSchema.valueType,
                    oldEnumTypeName,
                    newEnumTypeName
                  )
                }
              ]
            )
          )
        }
      ])
    )
  };
}
function removeEnum(schema, enumTypeName) {
  if (!schema.enums[enumTypeName]) {
    throw new Error(`enumTypeName does not exist: ${enumTypeName}`);
  }
  const fieldsToRemove = {};
  Object.entries(schema.objects).forEach(([objectName, objectType]) => {
    const objectFields = Object.keys(objectType.fields);
    const objectFieldsToRemove = objectFields.filter(
      (fieldName) => valueTypeReturnsEnum(objectType.fields[fieldName].valueType, enumTypeName)
    );
    if (getFieldArgumentsObjectTypeNameParts(objectName) === null && objectFieldsToRemove.length > 0 && objectFields.length - objectFieldsToRemove.length === 0) {
      throw new Error(
        `Removing enum "${enumTypeName}" would result in object "${objectName}" being empty. Delete it or add other fields to it first.`
      );
    }
    if (objectFieldsToRemove.length > 0) {
      fieldsToRemove[objectName] = objectFieldsToRemove;
    }
  });
  return {
    schema: {
      ...schema,
      enums: Object.fromEntries(
        Object.entries(schema.enums).filter(
          ([enumName]) => enumName !== enumTypeName
        )
      ),
      objects: removeFieldsFromObjects(schema, fieldsToRemove).objects
    },
    removedFields: fieldsToRemove
  };
}
function setEnumDescription(schema, enumTypeName, description) {
  return {
    ...schema,
    enums: Object.fromEntries(
      Object.entries(schema.enums).map(([enumName, objectSchema]) => [
        enumName,
        enumName === enumTypeName ? { ...objectSchema, description } : objectSchema
      ])
    )
  };
}
function setEnumValueDescription(schema, enumTypeName, valueName, description) {
  return setEnumValueSchema(schema, enumTypeName, valueName, (valueSchema) => ({
    ...valueSchema,
    description
  }));
}
function setEnumValueDeprecationReason(schema, enumTypeName, valueName, deprecationReason) {
  return setEnumValueSchema(schema, enumTypeName, valueName, (valueSchema) => {
    const { deprecationReason: _, ...baseValueSchema } = valueSchema;
    return {
      ...baseValueSchema,
      ...deprecationReason === void 0 ? {} : { deprecationReason }
    };
  });
}
function setEnumValueSchema(schema, enumTypeName, enumValueName, newValueSchema) {
  if (!schema.enums[enumTypeName]) {
    throw new Error(`enumTypeName does not exist: ${enumTypeName}`);
  }
  if (!schema.enums[enumTypeName].values[enumValueName]) {
    throw new Error(`valueName does not exist: ${enumValueName}`);
  }
  return {
    ...schema,
    enums: Object.fromEntries(
      Object.entries(schema.enums).map(([enumName, enumSchema]) => [
        enumName,
        enumName !== enumTypeName ? enumSchema : {
          ...enumSchema,
          values: Object.fromEntries(
            Object.entries(enumSchema.values).map(
              ([valueName, valueSchema]) => [
                valueName,
                enumValueName === valueName ? newValueSchema(valueSchema) : valueSchema
              ]
            )
          )
        }
      ])
    )
  };
}
function addEnumValue(schema, enumTypeName, rawNewValueName, description, valuePosition = "last") {
  const newValueName = formatEnumValueSchemaName(rawNewValueName);
  if (!isSchemaNameValid(newValueName)) {
    throw new Error(schemaNameErrorMessage("Enum value", newValueName));
  }
  if (!schema.enums[enumTypeName]) {
    throw new Error(`enumTypeName doesn't exist: ${enumTypeName}`);
  }
  if (schema.enums[enumTypeName].values[newValueName]) {
    throw new Error(alreadyExistsMessage("Enum value", newValueName));
  }
  const newValue = { [newValueName]: { description } };
  return {
    ...schema,
    enums: {
      ...schema.enums,
      [enumTypeName]: {
        ...schema.enums[enumTypeName],
        values: {
          ...valuePosition === "first" ? newValue : {},
          ...schema.enums[enumTypeName].values,
          ...valuePosition === "last" ? newValue : {}
        }
      }
    }
  };
}
function renameEnumValue(schema, enumTypeName, oldValueName, rawNewValueName) {
  const newValueName = formatEnumValueSchemaName(rawNewValueName);
  if (oldValueName === newValueName) {
    return schema;
  }
  if (!isSchemaNameValid(newValueName)) {
    throw new Error(schemaNameErrorMessage("Enum value", newValueName));
  }
  if (!schema.enums[enumTypeName].values[oldValueName]) {
    throw new Error(`oldValueName does not exist: ${oldValueName}`);
  }
  if (schema.enums[enumTypeName].values[newValueName]) {
    throw new Error(alreadyExistsMessage("Enum value", newValueName));
  }
  return {
    ...schema,
    enums: Object.fromEntries(
      Object.entries(schema.enums).map(([enumName, enumSchema]) => [
        enumName,
        enumTypeName === enumName ? {
          ...enumSchema,
          values: Object.fromEntries(
            Object.entries(enumSchema.values).map(([key, value]) => [
              key === oldValueName ? newValueName : key,
              value
            ])
          )
        } : enumSchema
      ])
    )
  };
}
function removeEnumValue(schema, enumTypeName, valueName) {
  if (!schema.enums[enumTypeName]) {
    throw new Error(`enumTypeName does not exist: ${enumTypeName}`);
  }
  if (!schema.enums[enumTypeName].values[valueName]) {
    throw new Error(`valueName does not exist: ${valueName}`);
  }
  if (Object.keys(schema.enums[enumTypeName].values).length === 1) {
    throw new Error(`enum must have at least one value: ${enumTypeName}`);
  }
  return {
    ...schema,
    enums: Object.fromEntries(
      Object.entries(schema.enums).map(([enumName, enumSchema]) => [
        enumName,
        enumTypeName === enumName ? {
          ...enumSchema,
          values: Object.fromEntries(
            Object.entries(enumSchema.values).filter(
              ([name]) => valueName !== name
            )
          )
        } : enumSchema
      ])
    )
  };
}
function formatEnumValueSchemaName(name) {
  return name.trim().split(/\s/).filter(Boolean).join("_");
}
function valueTypeReturnsEnum(valueType, enumTypeName) {
  switch (valueType.type) {
    case "EnumValueType":
      return valueType.enumTypeName === enumTypeName;
    case "FunctionValueType":
      return valueTypeReturnsEnum(valueType.returnValueType, enumTypeName);
    case "ListValueType":
      return valueTypeReturnsEnum(valueType.itemValueType, enumTypeName);
    default:
      return false;
  }
}

// src/schema/editableSchemaCode.ts
var editableSchemaCodeFilter = {
  type: "exclude",
  values: /* @__PURE__ */ new Set([queryObjectTypeName])
};
function splitSchemaCode(schema, schemaCode) {
  const hasDefaultQuerySchema = Object.keys(schema.objects[queryRootArgsObjectTypeName].fields).length === 1 && !!schema.objects[queryRootArgsObjectTypeName].fields.context;
  const readOnlySchemaCode = hasDefaultQuerySchema ? objectToCode(schema, queryObjectTypeName) : "";
  const editableSchemaCode = hasDefaultQuerySchema ? getSchemaCodeFromSchema(schema, editableSchemaCodeFilter) : schemaCode ?? getSchemaCodeFromSchema(schema);
  return { readOnlySchemaCode, editableSchemaCode };
}
function getFullSchemaCode(readOnlySchemaCode, editableSchemaCode) {
  return readOnlySchemaCode ? `${readOnlySchemaCode}

${editableSchemaCode}` : editableSchemaCode;
}
function removeSplitArmFromExpression(expression, splitId, dimensionId, armIdToDelete) {
  return mapExpression((expr) => {
    if (expr && expr.type === "SplitExpression" && expr.splitId === splitId && expr.dimensionId === dimensionId) {
      return {
        ...expr,
        dimensionMapping: expr.dimensionMapping.type === "discrete" ? {
          ...expr.dimensionMapping,
          cases: Object.fromEntries(
            Object.entries(expr.dimensionMapping.cases).filter(
              ([armId]) => armId !== armIdToDelete
            )
          )
        } : expr.dimensionMapping
      };
    }
    return expr;
  }, expression);
}
function renameEnumInExpression(expression, oldEnumTypeName, newEnumTypeName) {
  return mapExpression((expr) => {
    if (expr) {
      return {
        ...expr,
        valueType: renameEnumInValueType(
          expr.valueType,
          oldEnumTypeName,
          newEnumTypeName
        )
      };
    }
    return expr;
  }, expression);
}
function renameEnumValueInExpression(expression, enumTypeName, oldValueName, rawNewValueName) {
  const newValueName = formatEnumValueSchemaName(rawNewValueName);
  return mapExpression((expr) => {
    if (expr && expr.type === "EnumExpression" && expr.valueType.enumTypeName === enumTypeName && expr.value === oldValueName) {
      return {
        ...expr,
        value: newValueName
      };
    }
    if (expr && expr.type === "EnumSwitchExpression" && expr.control?.valueType.type === "EnumValueType" && expr.control?.valueType.enumTypeName === enumTypeName) {
      return {
        ...expr,
        value: newValueName,
        cases: Object.fromEntries(
          Object.entries(expr.cases).map(([enumValueName, field]) => [
            enumValueName === oldValueName ? newValueName : enumValueName,
            field
          ])
        )
      };
    }
    return expr;
  }, expression);
}
function renameObjectInExpression(expression, oldObjectTypeName, newObjectTypeName) {
  return mapExpression((expr) => {
    if (expr && expr.type === "ObjectExpression" && expr.objectTypeName === oldObjectTypeName) {
      return {
        ...expr,
        objectTypeName: newObjectTypeName,
        valueType: renameObjectInValueType(
          expr.valueType,
          oldObjectTypeName,
          newObjectTypeName
        )
      };
    }
    if (expr && expr.type === "SplitExpression" && expr.eventObjectTypeName === oldObjectTypeName) {
      return {
        ...expr,
        eventObjectTypeName: newObjectTypeName,
        valueType: renameObjectInValueType(
          expr.valueType,
          oldObjectTypeName,
          newObjectTypeName
        )
      };
    }
    if (expr && expr.type === "LogEventExpression" && expr.eventObjectTypeName === oldObjectTypeName) {
      return {
        ...expr,
        eventObjectTypeName: newObjectTypeName
      };
    }
    if (expr) {
      return {
        ...expr,
        valueType: renameObjectInValueType(
          expr.valueType,
          oldObjectTypeName,
          newObjectTypeName
        )
      };
    }
    return expr;
  }, expression);
}
function renameObjectFieldInExpression(schema, expression, objectTypeName, oldFieldName, rawNewFieldName) {
  const newFieldName = formatFieldSchemaName(rawNewFieldName);
  return mapExpression((expr) => {
    if (expr && expr.type === "ObjectExpression" && expr.objectTypeName === objectTypeName) {
      return {
        ...expr,
        fields: Object.fromEntries(
          Object.entries(expr.fields).map(
            ([fieldName, field]) => fieldName === oldFieldName ? [newFieldName, field] : [fieldName, field]
          )
        )
      };
    }
    if (expr && expr.type === "GetFieldExpression" && expr.fieldPath && expr.object?.valueType.type === "ObjectValueType") {
      return {
        ...expr,
        fieldPath: renameObjectFieldInPath(
          schema,
          objectTypeName,
          oldFieldName,
          newFieldName,
          expr.fieldPath,
          expr.object.valueType.objectTypeName
        )
      };
    }
    if (expr && expr.type === "UpdateObjectExpression" && expr.valueType.objectTypeName === objectTypeName) {
      return {
        ...expr,
        updates: Object.fromEntries(
          Object.entries(expr.updates).map(
            ([fieldName, field]) => fieldName === oldFieldName ? [newFieldName, field] : [fieldName, field]
          )
        )
      };
    }
    return expr;
  }, expression);
}
function removeObjectFieldsFromExpression(schema, expression, fieldsToRemove) {
  return mapExpression((expr) => {
    if (expr && expr.type === "ObjectExpression" && fieldsToRemove[expr.valueType.objectTypeName]) {
      return {
        ...expr,
        fields: Object.fromEntries(
          Object.entries(expr.fields).filter(
            ([fieldName]) => fieldsToRemove[expr.valueType.objectTypeName].indexOf(
              fieldName
            ) === -1
          )
        )
      };
    }
    if (expr && expr.type === "GetFieldExpression" && expr.fieldPath && expr.object?.valueType.type === "ObjectValueType") {
      const { fieldPath } = expr;
      const startObjectTypeName = expr.object.valueType.objectTypeName;
      const isAnyFieldInPath = Object.entries(fieldsToRemove).flatMap(
        ([objectTypeName, fieldNamesToRemove]) => fieldNamesToRemove.map(
          (fieldToRemove) => isObjectFieldInPath(
            schema,
            objectTypeName,
            fieldToRemove,
            fieldPath,
            startObjectTypeName
          )
        )
      ).some(Boolean);
      if (isAnyFieldInPath) {
        return null;
      }
      return expr;
    }
    if (expr && expr.type === "UpdateObjectExpression" && fieldsToRemove[expr.valueType.objectTypeName]) {
      return {
        ...expr,
        updates: Object.fromEntries(
          Object.entries(expr.updates).filter(
            ([fieldName]) => fieldsToRemove[expr.valueType.objectTypeName].indexOf(
              fieldName
            ) === -1
          )
        )
      };
    }
    return expr;
  }, expression);
}
function isObjectFieldInPath(schema, objectTypeName, fieldName, path, startObjectTypeName) {
  return walkObjectPath(
    schema,
    path,
    startObjectTypeName,
    (result, currentObjectTypeName, step) => {
      if (currentObjectTypeName === objectTypeName && step === fieldName) {
        return { stop: true, result: true };
      }
      return { stop: false, result };
    },
    false
  );
}
function renameObjectFieldInPath(schema, objectTypeName, oldFieldName, newFieldName, path, startObjectTypeName) {
  return walkObjectPath(
    schema,
    path,
    startObjectTypeName,
    (result, currentObjectTypeName, step) => {
      const currentStep = currentObjectTypeName === objectTypeName && step === oldFieldName ? newFieldName : step;
      return {
        result: !result ? currentStep : `${result}${fieldPathSeparator}${currentStep}`
      };
    },
    ""
  );
}
function walkObjectPath(schema, path, startObjectTypeName, evaluateCurrent, initial) {
  return path?.split(fieldPathSeparator).reduce(
    (value, step) => {
      if (value.stop) {
        return value;
      }
      if (!value.currentObjectTypeName) {
        return { stop: true, result: value.result };
      }
      const { stop, result } = evaluateCurrent(
        value.result,
        value.currentObjectTypeName,
        step
      );
      if (stop) {
        return { stop, result };
      }
      const currentValueType = schema.objects[value.currentObjectTypeName]?.fields[step]?.valueType;
      if (!currentValueType || currentValueType.type !== "ObjectValueType") {
        return { stop: true, result };
      }
      return {
        stop,
        result,
        currentObjectTypeName: currentValueType.objectTypeName
      };
    },
    { result: initial, currentObjectTypeName: startObjectTypeName }
  ).result;
}

// src/expression/splitOperations.ts
function deleteSplitDiscreteDimensionArm(splitMap, split, dimension, armId) {
  const { [armId]: deletedArm, ...newArms } = dimension.arms;
  return {
    ...splitMap,
    [split.id]: {
      ...split,
      dimensions: {
        ...split.dimensions,
        [dimension.id]: {
          ...dimension,
          arms: Object.fromEntries(
            Object.entries(newArms).map(
              ([, arm]) => arm.index < deletedArm.index ? [arm.id, arm] : [
                arm.id,
                {
                  ...arm,
                  index: arm.index - 1
                }
              ]
            )
          )
        }
      }
    }
  };
}
function copyExpression(expression) {
  return mapExpression((expr) => {
    if (!expr) {
      return null;
    }
    return {
      ...expr,
      id: uniqueId$1()
    };
  }, expression);
}
function sanitizeExpression(expression) {
  return mapExpression((expr) => {
    if (!expr) {
      return null;
    }
    if (expr.type === "SwitchExpression") {
      return {
        ...expr,
        id: "",
        isTransient: false,
        logs: {},
        cases: expr.cases.map((switchCase) => ({
          ...switchCase,
          id: ""
        })),
        metadata: void 0
      };
    }
    return {
      ...expr,
      id: "",
      isTransient: false,
      logs: {},
      metadata: void 0
    };
  }, expression);
}
function collectSplitAndEventTypeIds(expression) {
  const result = mapExpressionWithResult(
    (expr) => {
      const splitIds = new Array();
      const eventTypeIds = new Array();
      if (expr) {
        switch (expr?.type) {
          case "SplitExpression":
            if (expr.splitId) {
              splitIds.push(expr.splitId);
            }
            break;
          case "LogEventExpression":
            if (expr.eventTypeId) {
              eventTypeIds.push(expr.eventTypeId);
            }
            break;
        }
      }
      return {
        newExpression: expr,
        mapResult: {
          splitIds,
          eventTypeIds
        }
      };
    },
    (...results) => {
      return {
        splitIds: results.flatMap((res) => res.splitIds),
        eventTypeIds: results.flatMap((res) => res.eventTypeIds)
      };
    },
    expression
  );
  return {
    splitIds: new Set(result.mapResult.splitIds),
    eventTypeIds: new Set(result.mapResult.eventTypeIds)
  };
}

// src/expression/getApplicationFunctionExpression.ts
function getApplicationFunctionExpression(expression) {
  const functionExpression = expression.function;
  if (!functionExpression || functionExpression.type !== "FunctionExpression" || expression.arguments.length !== functionExpression.valueType.parameterValueTypes.length || expression.arguments.length !== functionExpression.parameters.length) {
    return null;
  }
  return functionExpression;
}

// src/expression/dropArgument.ts
function dropArgument(expression, index) {
  const argument = expression.arguments[index];
  const functionExpression = getApplicationFunctionExpression(expression);
  if (!argument || !functionExpression) {
    return null;
  }
  const parameter = functionExpression.parameters[index];
  const { body } = functionExpression;
  if (!parameter || !body) {
    return null;
  }
  const newBody = mapExpression((expr) => {
    if (expr && expr.type === "VariableExpression" && expr.variableId === parameter.id) {
      return copyExpression(argument);
    }
    return expr;
  }, body);
  const newFunctionExpression = {
    ...functionExpression,
    valueType: {
      ...functionExpression.valueType,
      parameterValueTypes: [
        ...functionExpression.valueType.parameterValueTypes.slice(0, index),
        ...functionExpression.valueType.parameterValueTypes.slice(index + 1)
      ]
    },
    parameters: [
      ...functionExpression.parameters.slice(0, index),
      ...functionExpression.parameters.slice(index + 1)
    ],
    body: newBody
  };
  const newExpression = {
    ...expression,
    function: newFunctionExpression,
    arguments: [
      ...expression.arguments.slice(0, index),
      ...expression.arguments.slice(index + 1)
    ]
  };
  return newExpression;
}
function usesVariables(expression, variableIds) {
  const result = mapExpressionWithResult(
    (expr) => {
      if (expr && expr.type === "VariableExpression" && variableIds[expr.variableId]) {
        return {
          newExpression: expr,
          mapResult: true
        };
      }
      return {
        newExpression: expr,
        mapResult: false
      };
    },
    (...results) => results.some((b) => b),
    expression
  );
  return result.mapResult;
}
function getComparisonExpression() {
  const expression = {
    id: uniqueId$1(),
    type: "ComparisonExpression",
    valueType: { type: "BooleanValueType" },
    operator: null,
    a: null,
    b: null
  };
  return expression;
}

// src/expression/constraint/getConstraintFromValueType.ts
function getConstraintFromValueType(valueType) {
  switch (valueType.type) {
    case "VoidValueType":
      return { type: "VoidValueTypeConstraint" };
    case "BooleanValueType":
      return { type: "BooleanValueTypeConstraint" };
    case "IntValueType":
      return { type: "IntValueTypeConstraint" };
    case "FloatValueType":
      return { type: "FloatValueTypeConstraint" };
    case "StringValueType":
      return { type: "StringValueTypeConstraint" };
    case "RegexValueType":
      return { type: "RegexValueTypeConstraint" };
    case "EnumValueType":
      return {
        type: "EnumValueTypeConstraint",
        enumTypeName: valueType.enumTypeName
      };
    case "ObjectValueType":
      return {
        type: "ObjectValueTypeConstraint",
        objectTypeName: valueType.objectTypeName
      };
    case "UnionValueType":
      return {
        type: "UnionValueTypeConstraint",
        unionTypeName: valueType.unionTypeName
      };
    case "ListValueType":
      return {
        type: "ListValueTypeConstraint",
        itemValueTypeConstraint: getConstraintFromValueType(
          valueType.itemValueType
        )
      };
    case "FunctionValueType":
      return {
        type: "FunctionValueTypeConstraint",
        parameterValueTypeConstraints: valueType.parameterValueTypes.map(
          (parameterValueType) => getConstraintFromValueType(parameterValueType)
        ),
        returnValueTypeConstraint: getConstraintFromValueType(
          valueType.returnValueType
        )
      };
    default: {
      const neverValueType = valueType;
      throw new Error(
        `Unexpected value type type: ${JSON.stringify(neverValueType)}`
      );
    }
  }
}
function getListExpression(valueType) {
  const expression = {
    id: uniqueId$1(),
    type: "ListExpression",
    valueType,
    items: []
  };
  return expression;
}

// src/expression/getNewVariableName.ts
function getNewVariableName(existingNames, valueType) {
  return getNextName(existingNames, getBaseName(valueType));
}
function getBaseName(valueType) {
  switch (valueType.type) {
    case "VoidValueType":
      return "voidVar";
    case "BooleanValueType":
      return "booleanVar";
    case "IntValueType":
      return "intVar";
    case "FloatValueType":
      return "floatVar";
    case "StringValueType":
      return "stringVar";
    case "RegexValueType":
      return "regexVar";
    case "EnumValueType":
      return `${toCamelCase(valueType.enumTypeName)}Var`;
    case "ObjectValueType": {
      const fieldArgumentsObjectTypeNameParts = getFieldArgumentsObjectTypeNameParts(valueType.objectTypeName);
      return toCamelCase(
        fieldArgumentsObjectTypeNameParts ? `${fieldArgumentsObjectTypeNameParts.fieldName}Args` : `${valueType.objectTypeName}Var`
      );
    }
    case "UnionValueType":
      return `${toCamelCase(valueType.unionTypeName)}Var`;
    case "ListValueType":
      return "listVar";
    case "FunctionValueType":
      return "functionVar";
    default: {
      const neverParameterValueType = valueType;
      throw new Error(
        `Unexpected parameter value type: ${neverParameterValueType}`
      );
    }
  }
}

// src/expression/getVariableNames.ts
function getVariableNames(variables) {
  const variableNames = {};
  Object.values(variables).forEach((variable) => {
    variableNames[variable.name] = true;
  });
  return variableNames;
}

// src/expression/getParameters.ts
function getParameters(variables, parameterValueTypes) {
  const parameters = [];
  const variableNames = getVariableNames(variables);
  parameterValueTypes.forEach((parameterValueType) => {
    const name = getNewVariableName(variableNames, parameterValueType);
    const parameter = {
      id: uniqueId$1(),
      name
    };
    parameters.push(parameter);
    variableNames[name] = true;
  });
  return parameters;
}

// src/filterNulls.ts
function filterNulls(array) {
  return array.filter((x) => typeof x !== "undefined" && x !== null);
}

// src/expression/constraint/getValueTypeFromConstraint.ts
function getValueTypeFromConstraint(valueTypeConstraint) {
  switch (valueTypeConstraint.type) {
    case "VoidValueTypeConstraint":
      return { type: "VoidValueType" };
    case "BooleanValueTypeConstraint":
      return { type: "BooleanValueType" };
    case "IntValueTypeConstraint":
      return { type: "IntValueType" };
    case "FloatValueTypeConstraint":
      return { type: "FloatValueType" };
    case "StringValueTypeConstraint":
      return { type: "StringValueType" };
    case "RegexValueTypeConstraint":
      return { type: "RegexValueType" };
    case "EnumValueTypeConstraint":
      return {
        type: "EnumValueType",
        enumTypeName: valueTypeConstraint.enumTypeName
      };
    case "AnyEnumValueTypeConstraint":
      return null;
    case "ObjectValueTypeConstraint":
      return {
        type: "ObjectValueType",
        objectTypeName: valueTypeConstraint.objectTypeName
      };
    case "AnyObjectValueTypeConstraint":
      return null;
    case "UnionValueTypeConstraint":
      return {
        type: "UnionValueType",
        unionTypeName: valueTypeConstraint.unionTypeName
      };
    case "ListValueTypeConstraint": {
      const listItemValueType = getValueTypeFromConstraint(
        valueTypeConstraint.itemValueTypeConstraint
      );
      if (!listItemValueType) {
        return null;
      }
      return {
        type: "ListValueType",
        itemValueType: listItemValueType
      };
    }
    case "FunctionValueTypeConstraint": {
      const parameterValueTypes = valueTypeConstraint.parameterValueTypeConstraints.map(
        (constraint) => getValueTypeFromConstraint(constraint)
      );
      const filteredParameterValueTypes = filterNulls(parameterValueTypes);
      const returnValueType = getValueTypeFromConstraint(
        valueTypeConstraint.returnValueTypeConstraint
      );
      if (parameterValueTypes.length !== filteredParameterValueTypes.length || !returnValueType) {
        return null;
      }
      return {
        type: "FunctionValueType",
        parameterValueTypes: filteredParameterValueTypes,
        returnValueType
      };
    }
    case "FunctionWithReturnValueTypeConstraint":
    case "AnyNonFunctionValueTypeConstraint":
    case "AnyValueTypeConstraint":
      return null;
    default: {
      const neverValueTypeConstraint = valueTypeConstraint;
      throw new Error(
        `Unexpected inner value type constraint: ${JSON.stringify(
          neverValueTypeConstraint
        )}`
      );
    }
  }
}
var primitiveExpressionTypes = [
  BooleanExpressionType,
  IntExpressionType,
  FloatExpressionType,
  StringExpressionType,
  RegexExpressionType,
  EnumExpressionType
];
function isPrimitiveExpression(expression) {
  return !!expression && primitiveExpressionTypes.includes(expression.type);
}
function getLogEventExpression(eventObjectTypeName, eventTypeId) {
  return {
    id: uniqueId$1(),
    type: "LogEventExpression",
    valueType: { type: "VoidValueType" },
    eventTypeId,
    unitId: getStringExpression(""),
    featuresMapping: {},
    eventObjectTypeName,
    eventPayload: null
  };
}
function getContextExpression(contextTypeName, rootArgsVariableId) {
  return {
    id: uniqueId$2(),
    type: "GetFieldExpression",
    fieldPath: "context",
    valueType: {
      type: "ObjectValueType",
      objectTypeName: contextTypeName
    },
    object: {
      id: uniqueId$2(),
      type: "VariableExpression",
      variableId: rootArgsVariableId,
      valueType: {
        type: "ObjectValueType",
        objectTypeName: queryRootArgsTypeName
      }
    }
  };
}

// src/expression/getExpressionOptionGroups.ts
function getDefaultUnitIdExpression(schema, variables) {
  const rootArgsVariableId = Object.values(variables).find(
    (variable) => variable.valueType.type === "ObjectValueType" && variable.valueType.objectTypeName === queryRootArgsTypeName
  )?.id;
  const unitIdPathOptions = getStringValuePathsForObject(
    schema,
    queryRootArgsTypeName
  ).filter(
    (path) => path.length === 3 && path[0] === "context" && contextInputsSet.has(path[1]) && contextInputFieldsSet.has(path[2])
  ).sort(
    (a, b) => 100 * (contextInputsWeights[a[1]] - contextInputsWeights[b[1]]) + (contextInputFieldsWeights[a[2]] - contextInputFieldsWeights[b[2]])
  );
  return rootArgsVariableId && unitIdPathOptions.length > 0 ? {
    id: uniqueId$1(),
    type: "GetFieldExpression",
    valueType: { type: "StringValueType" },
    fieldPath: unitIdPathOptions[0].join(fieldPathSeparator),
    object: {
      id: uniqueId$1(),
      type: "VariableExpression",
      variableId: rootArgsVariableId,
      valueType: {
        type: "ObjectValueType",
        objectTypeName: queryRootArgsTypeName
      }
    }
  } : null;
}
function getBaseExpressionOptionsForValueType(schema, variables, valueType, seenObjectTypeNames, options) {
  switch (valueType.type) {
    case "VoidValueType": {
      return [getLogEventExpression(null, null), getNoOpExpression()];
    }
    case "BooleanValueType": {
      const booleanExpression = getBooleanExpression(
        !!options?.defaultBooleanValue
      );
      return [booleanExpression];
    }
    case "IntValueType": {
      const intExpression = getIntExpression(0);
      const arithmeticExpression = {
        id: uniqueId$1(),
        type: "ArithmeticExpression",
        valueType,
        operator: "+",
        a: null,
        b: null
      };
      const roundNumberExpression = getRoundNumberExpression();
      return [intExpression, arithmeticExpression, roundNumberExpression];
    }
    case "FloatValueType": {
      const floatExpression = getFloatExpression(0);
      const arithmeticExpression = {
        id: uniqueId$1(),
        type: "ArithmeticExpression",
        valueType,
        operator: "+",
        a: null,
        b: null
      };
      const roundNumberExpression = getRoundNumberExpression();
      return [floatExpression, arithmeticExpression, roundNumberExpression];
    }
    case "StringValueType": {
      const stringExpression = getStringExpression("");
      const stringConcatExpression = {
        id: uniqueId$1(),
        type: "StringConcatExpression",
        valueType,
        strings: getListExpression({
          type: "ListValueType",
          itemValueType: valueType
        })
      };
      const stringifyNumberExpression = {
        id: uniqueId$1(),
        type: "StringifyNumberExpression",
        valueType,
        number: null
      };
      const getUrlQueryParameterExpression = {
        id: uniqueId$1(),
        type: "GetUrlQueryParameterExpression",
        valueType,
        url: null,
        queryParameterName: null
      };
      return [
        stringExpression,
        stringConcatExpression,
        stringifyNumberExpression,
        getUrlQueryParameterExpression
      ];
    }
    case "RegexValueType": {
      const expression = {
        id: uniqueId$1(),
        type: "RegexExpression",
        valueType: { type: "RegexValueType" },
        value: ""
      };
      return [expression];
    }
    case "EnumValueType": {
      const schemaEnumValues = schema.enums[valueType.enumTypeName]?.values;
      if (!schemaEnumValues || Object.keys(schemaEnumValues).length === 0) {
        return [];
      }
      const expression = {
        id: uniqueId$1(),
        type: "EnumExpression",
        valueType,
        value: Object.keys(schemaEnumValues)[0]
      };
      return [expression];
    }
    case "ObjectValueType": {
      const updateObjectExpression = {
        id: uniqueId$1(),
        type: "UpdateObjectExpression",
        valueType,
        object: getObjectExpression(
          schema,
          variables,
          valueType,
          seenObjectTypeNames,
          options
        ),
        updates: {}
      };
      return [
        getObjectExpression(
          schema,
          variables,
          valueType,
          seenObjectTypeNames,
          options
        ),
        updateObjectExpression
      ];
    }
    case "UnionValueType": {
      const schemaUnionMembers = schema.unions[valueType.unionTypeName]?.variants;
      if (!schemaUnionMembers) {
        return [];
      }
      return Object.keys(schemaUnionMembers).map((objectTypeName) => {
        const expression = getObjectExpression(
          schema,
          variables,
          { type: "ObjectValueType", objectTypeName },
          seenObjectTypeNames,
          options
        );
        return expression;
      });
    }
    case "ListValueType": {
      const expression = getListExpression(valueType);
      const initialItem = getDefaultExpression(
        schema,
        variables,
        getConstraintFromValueType(valueType.itemValueType),
        seenObjectTypeNames,
        options
      );
      if (isPrimitiveExpression(initialItem)) {
        expression.items.push(initialItem);
      }
      return [expression];
    }
    case "FunctionValueType": {
      const parameters = getParameters(
        variables,
        valueType.parameterValueTypes
      );
      const expression = {
        id: uniqueId$1(),
        type: "FunctionExpression",
        valueType,
        parameters,
        body: getDefaultExpression(
          schema,
          {
            ...variables,
            ...Object.fromEntries(
              parameters.map((parameter, index) => [
                parameter.id,
                {
                  ...parameter,
                  valueType: valueType.parameterValueTypes[index]
                }
              ])
            )
          },
          getConstraintFromValueType(valueType.returnValueType),
          seenObjectTypeNames,
          options
        )
      };
      return [expression];
    }
    default: {
      const neverValueType = valueType;
      throw new Error(`Unexpected value type: ${neverValueType}`);
    }
  }
}
function getRoundNumberExpression() {
  const expression = {
    id: uniqueId$1(),
    type: "RoundNumberExpression",
    valueType: { type: "IntValueType" },
    number: null
  };
  return expression;
}
function getObjectExpression(schema, variables, valueType, seenObjectTypeNames, options) {
  const { objectTypeName } = valueType;
  const shouldRecurse = !seenObjectTypeNames.has(objectTypeName);
  seenObjectTypeNames.add(objectTypeName);
  const schemaObjectFields = schema.objects[objectTypeName]?.fields || {};
  const fields = Object.fromEntries(
    Object.entries(schemaObjectFields).map(([fieldName, field]) => {
      if (schema.objects[objectTypeName]?.role === "event" && fieldName === "context") {
        const contextTypeName = rootContextTypeNameFromSchema(schema);
        const rootArgsVariableId = Object.values(variables).find(
          (variable) => variable.valueType.type === "ObjectValueType" && variable.valueType.objectTypeName === queryRootArgsTypeName
        )?.id;
        if (contextTypeName && rootArgsVariableId && field.valueType.type === "ObjectValueType" && field.valueType.objectTypeName === contextTypeName) {
          return [
            fieldName,
            getContextExpression(contextTypeName, rootArgsVariableId)
          ];
        }
      }
      return [
        fieldName,
        shouldRecurse ? getDefaultExpression(
          schema,
          variables,
          getConstraintFromValueType(field.valueType),
          seenObjectTypeNames,
          options
        ) : null
      ];
    })
  );
  seenObjectTypeNames.delete(objectTypeName);
  return {
    id: uniqueId$1(),
    type: "ObjectExpression",
    valueType,
    objectTypeName,
    fields
  };
}

// src/expression/getDefaultExpression.ts
function getDefaultExpression(schema, variables, valueTypeConstraint, seenObjectTypeNames, options) {
  if (valueTypeConstraint.type === "ErrorValueTypeConstraint") {
    return null;
  }
  const valueType = getValueTypeFromConstraint(valueTypeConstraint);
  if (!valueType || valueType.type === "UnionValueType") {
    return null;
  }
  const expression = getBaseExpressionOptionsForValueType(
    schema,
    variables,
    valueType,
    seenObjectTypeNames,
    options
  )[0] || null;
  return expression;
}

// src/expression/getIfExpression.ts
function getIfExpression(schema, variables, valueType, options, defaultValue) {
  const trueExpression = getBooleanExpression(true);
  const initialCase = {
    id: uniqueId$1(),
    when: getComparisonExpression(),
    then: getDefaultExpression(
      schema,
      variables,
      getConstraintFromValueType(valueType),
      /* @__PURE__ */ new Set(),
      options
    )
  };
  const defaultExpression = defaultValue ?? (valueType.type === "BooleanValueType" ? getBooleanExpression(false) : getDefaultExpression(
    schema,
    variables,
    getConstraintFromValueType(valueType),
    /* @__PURE__ */ new Set(),
    options
  ));
  const ifElseExpression = {
    id: uniqueId$1(),
    type: "SwitchExpression",
    valueType,
    control: trueExpression,
    cases: [initialCase],
    default: defaultExpression
  };
  return ifElseExpression;
}

// src/expression/isIfElseExpression.ts
function isIfElseExpression(expression) {
  return expression.type === "SwitchExpression" && !!expression.control && expression.control.type === "BooleanExpression" && expression.control.value;
}

// src/expression/isEmptyExpression.ts
function isEmptyExpression(expression) {
  if (!expression) {
    return true;
  }
  if (expression.metadata?.note || expression.metadata?.permissions) {
    return false;
  }
  switch (expression.type) {
    case "BooleanExpression":
      return !expression.value;
    case "StringExpression":
      return !expression.value;
    case "ListExpression":
      return expression.items.every((item) => isEmptyExpression(item));
    case "ObjectExpression":
      return Object.values(expression.fields).every(
        (field) => isEmptyExpression(field)
      );
    case "IntExpression":
    case "FloatExpression":
      return expression.value === 0;
    case "SwitchExpression":
      return expression.cases.length === 0 && expression.control?.type === "BooleanExpression" && expression.control.value && isEmptyExpression(expression.default);
    case "FunctionExpression":
      return isEmptyExpression(expression.body);
    default:
      return false;
  }
}
function getEmptyExpression(schema, variables, valueType, defaultValues) {
  if (valueType.type !== "FunctionValueType") {
    return null;
  }
  const parameters = getParameters(variables, valueType.parameterValueTypes);
  const functionExpression = {
    id: uniqueId$1(),
    type: "FunctionExpression",
    valueType,
    parameters,
    body: null
  };
  if (defaultValues?.split) {
    functionExpression.body = {
      id: uniqueId$1(),
      type: "SplitExpression",
      valueType: valueType.returnValueType,
      splitId: defaultValues.split.id,
      dimensionId: defaultValues.split.dimensionId,
      expose: getBooleanExpression(true),
      unitId: getDefaultUnitIdExpression(schema, defaultValues.split.variables),
      dimensionMapping: {
        type: "discrete",
        cases: {}
      },
      eventObjectTypeName: null,
      eventPayload: null,
      featuresMapping: {}
    };
    return functionExpression;
  }
  if (defaultValues) {
    switch (valueType.returnValueType.type) {
      case "BooleanValueType":
        if (defaultValues.boolean !== void 0) {
          functionExpression.body = getBooleanExpression(defaultValues.boolean);
        }
        break;
      case "StringValueType":
        if (defaultValues.string !== void 0) {
          functionExpression.body = getStringExpression(defaultValues.string);
        }
        break;
      case "IntValueType":
        if (defaultValues.int !== void 0) {
          functionExpression.body = getIntExpression(defaultValues.int);
        }
        break;
      case "FloatValueType":
        if (defaultValues.float !== void 0) {
          functionExpression.body = getFloatExpression(defaultValues.float);
        }
        break;
      case "VoidValueType":
        if (defaultValues.eventTypeNameAndId) {
          const { name, id, payload } = defaultValues.eventTypeNameAndId;
          functionExpression.body = getLogEventExpression(name, id);
          functionExpression.body.unitId = getStringExpression("");
          if (payload) {
            functionExpression.body.eventPayload = {
              id: uniqueId$1(),
              type: "ObjectExpression",
              valueType: { type: "ObjectValueType", objectTypeName: name },
              objectTypeName: name,
              fields: {
                context: getContextExpression(
                  payload.contextTypeName,
                  payload.rootArgsVariableId
                )
              }
            };
          }
        }
        break;
      case "EnumValueType":
        if (defaultValues.complexTypes) {
          const enumExpression = getDefaultExpression(
            schema,
            variables,
            getConstraintFromValueType(valueType.returnValueType),
            /* @__PURE__ */ new Set()
          );
          functionExpression.body = enumExpression;
        }
        break;
      case "ObjectValueType":
        if (defaultValues.complexTypes) {
          functionExpression.body = getDefaultExpression(
            schema,
            variables,
            getConstraintFromValueType(valueType.returnValueType),
            /* @__PURE__ */ new Set()
          );
        }
        break;
      case "ListValueType":
        if (defaultValues.complexTypes) {
          functionExpression.body = getDefaultExpression(
            schema,
            variables,
            getConstraintFromValueType(valueType.returnValueType),
            /* @__PURE__ */ new Set()
          );
        }
        break;
    }
  }
  return functionExpression;
}
function isExpressionEqualExceptIdAndChildren(a, b) {
  if (a === b) {
    return true;
  }
  if (a.type !== b.type) {
    return false;
  }
  switch (a.type) {
    case "NoOpExpression":
    case "BooleanExpression":
    case "IntExpression":
    case "FloatExpression":
    case "StringExpression":
    case "RegexExpression":
    case "EnumExpression":
    case "VariableExpression": {
      const { id: aId, ...aExceptExcluded } = a;
      const { id: bId, ...bExceptExcluded } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "ObjectExpression": {
      const { id: aId, fields: aFields, ...aExceptExcluded } = a;
      const {
        id: bId,
        fields: bFields,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && deepEqualsPlainObject(Object.keys(aFields), Object.keys(bFields));
    }
    case "GetFieldExpression": {
      const { id: aId, object: aObject, ...aExceptExcluded } = a;
      const {
        id: bId,
        object: bObject,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "UpdateObjectExpression": {
      const {
        id: aId,
        object: aObject,
        updates: aUpdates,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        object: bObject,
        updates: bUpdates,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && deepEqualsPlainObject(Object.keys(aUpdates), Object.keys(bUpdates));
    }
    case "ListExpression": {
      const { id: aId, items: aItems, ...aExceptExcluded } = a;
      const {
        id: bId,
        items: bItems,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && aItems.length === bItems.length;
    }
    case "SwitchExpression": {
      const {
        id: aId,
        control: aControl,
        cases: aCases,
        default: aDefault,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        control: bControl,
        cases: bCases,
        default: bDefault,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && aCases.length === bCases.length;
    }
    case "EnumSwitchExpression": {
      const { id: aId, control: aItems, cases: aCases, ...aExceptExcluded } = a;
      const {
        id: bId,
        control: bItems,
        cases: bCases,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && deepEqualsPlainObject(Object.keys(aCases), Object.keys(bCases));
    }
    case "ComparisonExpression":
    case "ArithmeticExpression": {
      const { id: aId, a: aA, b: aB, ...aExceptExcluded } = a;
      const {
        id: bId,
        a: bA,
        b: bB,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "RoundNumberExpression":
    case "StringifyNumberExpression": {
      const { id: aId, number: aNumber, ...aExceptExcluded } = a;
      const {
        id: bId,
        number: bNumber,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "StringConcatExpression": {
      const { id: aId, strings: aStrings, ...aExceptExcluded } = a;
      const {
        id: bId,
        strings: bStrings,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "GetUrlQueryParameterExpression": {
      const {
        id: aId,
        url: aUrl,
        queryParameterName: aQueryParameterName,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        url: bUrl,
        queryParameterName: bQueryParameterName,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "SplitExpression": {
      const {
        id: aId,
        expose: aExpose,
        unitId: aUnitId,
        dimensionMapping: aDimensionMapping,
        featuresMapping: aFeaturesMapping,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        expose: bExpose,
        unitId: bUnitId,
        dimensionMapping: bDimensionMapping,
        featuresMapping: bFeaturesMapping,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && aDimensionMapping.type === bDimensionMapping.type && (aDimensionMapping.type === "discrete" && bDimensionMapping.type === "discrete" ? stableStringify(Object.keys(aDimensionMapping.cases)) === stableStringify(Object.keys(bDimensionMapping.cases)) : true) && stableStringify(Object.keys(aFeaturesMapping)) === stableStringify(Object.keys(bFeaturesMapping));
    }
    case "LogEventExpression": {
      const {
        id: aId,
        unitId: aUnitId,
        featuresMapping: aFeaturesMapping,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        unitId: bUnitId,
        featuresMapping: bFeaturesMapping,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && stableStringify(Object.keys(aFeaturesMapping)) === stableStringify(Object.keys(bFeaturesMapping));
    }
    case "FunctionExpression": {
      const { id: aId, body: aBody, ...aExceptExcluded } = a;
      const {
        id: bId,
        body: bBody,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded);
    }
    case "ApplicationExpression": {
      const {
        id: aId,
        function: aFunction,
        arguments: aArguments,
        ...aExceptExcluded
      } = a;
      const {
        id: bId,
        function: bFunction,
        arguments: bArguments,
        ...bExceptExcluded
      } = b;
      return deepEqualsPlainObject(aExceptExcluded, bExceptExcluded) && aArguments.length === bArguments.length;
    }
    default: {
      const neverExpression = a;
      throw new Error(
        `Unexpected expression: ${JSON.stringify(neverExpression)}`
      );
    }
  }
}

// src/getExpressionChildren.ts
function getExpressionChildren(expression) {
  switch (expression.type) {
    case "NoOpExpression":
    case "BooleanExpression":
    case "IntExpression":
    case "FloatExpression":
    case "StringExpression":
    case "RegexExpression":
    case "EnumExpression":
      return [];
    case "ObjectExpression":
      return Object.values(expression.fields);
    case "GetFieldExpression":
      return [expression.object];
    case "UpdateObjectExpression":
      return [expression.object, ...Object.values(expression.updates)];
    case "ListExpression":
      return expression.items;
    case "SwitchExpression":
      return [
        expression.control,
        ...expression.cases.flatMap((item) => [item.then, item.when]),
        expression.default
      ];
    case "EnumSwitchExpression":
      return [expression.control, ...Object.values(expression.cases)];
    case "ComparisonExpression":
    case "ArithmeticExpression":
      return [expression.a, expression.b];
    case "RoundNumberExpression":
    case "StringifyNumberExpression":
      return [expression.number];
    case "StringConcatExpression":
      return [expression.strings];
    case "GetUrlQueryParameterExpression":
      return [expression.url, expression.queryParameterName];
    case "SplitExpression":
      return [
        expression.expose,
        expression.unitId,
        ...expression.dimensionMapping.type === "discrete" ? Object.values(expression.dimensionMapping.cases) : [expression.dimensionMapping.function],
        ...Object.values(expression.featuresMapping)
      ];
    case "LogEventExpression":
      return [expression.unitId, ...Object.values(expression.featuresMapping)];
    case "FunctionExpression":
      return [expression.body];
    case "VariableExpression":
      return [];
    case "ApplicationExpression":
      return [expression.function, ...expression.arguments];
    default: {
      const neverExpression = expression;
      throw new Error(
        `unexpected expression: ${JSON.stringify(neverExpression)}`
      );
    }
  }
}

// src/permissions.ts
function resolvePermissions(parent, current) {
  if (!current) {
    return parent;
  }
  return {
    group: { ...parent.group, ...current.group },
    user: { ...parent.user, ...current.user }
  };
}
function getEmptyPermissions() {
  return { group: {}, user: {} };
}
function isEmptyPermissions(permissions) {
  if (!permissions) {
    return true;
  }
  return Object.keys(permissions.user).length === 0 && Object.keys(permissions.group).length === 0;
}
function canWrite(userId, resolvedPermissions) {
  return resolvedPermissions.group[teamGroupId]?.write === "allow" || resolvedPermissions.user[userId]?.write === "allow";
}
function isAllowed(userId, parentPermissions, original, updated) {
  if (original === updated) {
    return true;
  }
  const perms = resolvePermissions(
    parentPermissions,
    original?.metadata?.permissions
  );
  if (canWrite(userId, perms)) {
    return true;
  }
  if (!original || !updated) {
    return false;
  }
  if (!isExpressionEqualExceptIdAndChildren(original, updated)) {
    return false;
  }
  const originalChildren = getExpressionChildren(original);
  const updatedChildren = getExpressionChildren(updated);
  return (
    // Check we have the same number of children...
    originalChildren.length === updatedChildren.length && // ...in the same place with only allowed edits
    originalChildren.every(
      (child, i) => isAllowed(userId, perms, child, updatedChildren[i])
    )
  );
}

// src/expression/getArmAllocationsSum.ts
function getArmAllocationsSum(dimension) {
  return Object.values(dimension.arms).reduce(
    (acc, curr) => acc + curr.allocation,
    0
  );
}

// src/expression/isDefaultArmNeeded.ts
function isDefaultArmNeeded(split, dimension) {
  return !!split && split.type === "test" && !!dimension && dimension.type === "discrete" && getArmAllocationsSum(dimension) < 1;
}

// src/expression/isValueTypeValid.ts
function isValueTypeValid(schema, valueType) {
  switch (valueType.type) {
    case "VoidValueType":
    case "BooleanValueType":
    case "IntValueType":
    case "FloatValueType":
    case "StringValueType":
    case "RegexValueType":
      return true;
    case "EnumValueType":
      return !!schema.enums[valueType.enumTypeName];
    case "ObjectValueType":
      return !!schema.objects[valueType.objectTypeName];
    case "UnionValueType":
      return !!schema.unions[valueType.unionTypeName];
    case "ListValueType":
      return isValueTypeValid(schema, valueType.itemValueType);
    case "FunctionValueType":
      return valueType.parameterValueTypes.every(
        (parameterValueType) => isValueTypeValid(schema, parameterValueType)
      ) && isValueTypeValid(schema, valueType.returnValueType);
    default: {
      const neverValueType = valueType;
      throw new Error(
        `Unexpected value type: ${JSON.stringify(neverValueType)}`
      );
    }
  }
}

// src/expression/fixAndSimplify.ts
function fixAndSimplify(schema, splitMap, eventTypeMap, defaultValues, expression) {
  let result = fixAndSimplifyOnce(
    schema,
    splitMap,
    eventTypeMap,
    defaultValues,
    expression
  );
  let hasChanged = false;
  while (result.numChanges > 0) {
    hasChanged = true;
    result = fixAndSimplifyOnce(
      schema,
      splitMap,
      eventTypeMap,
      defaultValues,
      result.newExpression
    );
  }
  return {
    newExpression: result.newExpression,
    hasChanged
  };
}
function fixAndSimplifyOnce(schema, splitMap, eventTypeMap, defaultValues, expression) {
  const result = mapExpressionWithResult(
    // Typescript seems to fail here occasionally for some reason.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (expr) => {
      if (!expr) {
        return { newExpression: expr, mapResult: 0 };
      }
      if (expr.type === "ObjectExpression") {
        if (expr.objectTypeName !== expr.valueType.objectTypeName) {
          return {
            newExpression: {
              ...expr,
              objectTypeName: expr.valueType.objectTypeName
            },
            mapResult: 1
          };
        }
        const schemaObjectFields = schema.objects[expr.objectTypeName]?.fields || {};
        const expressionFieldNames = Object.keys(expr.fields);
        const extraFieldNames = expressionFieldNames.filter(
          (fieldName) => !schemaObjectFields[fieldName]
        );
        const autoRemovableFieldNames = extraFieldNames.filter(
          (fieldName) => isEmptyExpression(expr.fields[fieldName])
        );
        const autoAddableFieldNames = Object.keys(schemaObjectFields).filter(
          (fieldName) => !expressionFieldNames.includes(fieldName)
        );
        if (autoRemovableFieldNames.length > 0 || autoAddableFieldNames.length > 0) {
          const newFields = Object.fromEntries([
            ...Object.entries(expr.fields).filter(
              ([fieldName]) => !autoRemovableFieldNames.includes(fieldName)
            ),
            ...autoAddableFieldNames.map((fieldName) => {
              const newExpression = getEmptyExpression(
                schema,
                {},
                schemaObjectFields[fieldName].valueType,
                defaultValues
              );
              return [fieldName, newExpression];
            })
          ]);
          return {
            newExpression: {
              ...expr,
              fields: newFields
            },
            mapResult: 1
          };
        }
      }
      if (expr.type === "ApplicationExpression") {
        const functionExpression = getApplicationFunctionExpression(expr);
        if (functionExpression) {
          if (functionExpression.body) {
            if (expr.arguments.length === 0) {
              return {
                newExpression: {
                  ...functionExpression.body,
                  metadata: {
                    permissions: resolvePermissions(
                      expr.metadata?.permissions ?? getEmptyPermissions(),
                      resolvePermissions(
                        functionExpression.metadata?.permissions ?? getEmptyPermissions(),
                        functionExpression.body?.metadata?.permissions
                      )
                    ),
                    note: combineNotes(
                      expr.metadata?.note,
                      functionExpression.metadata?.note,
                      functionExpression.body?.metadata?.note
                    )
                  }
                },
                mapResult: 1
              };
            }
          }
          for (let i = 0; i < expr.arguments.length; i += 1) {
            const argument = expr.arguments[i];
            if (argument && (argument.type === "VariableExpression" || isRedundantFunctionExpression(argument))) {
              const newExpression = dropArgument(expr, i);
              if (newExpression) {
                return { newExpression, mapResult: 1 };
              }
            }
          }
          if (functionExpression.body && functionExpression.body.type === "ApplicationExpression") {
            const innerApplication = functionExpression.body;
            const outerVariableIds = Object.fromEntries(
              functionExpression.parameters.map((parameter) => [
                parameter.id,
                true
              ])
            );
            const innerFunctionExpression = getApplicationFunctionExpression(innerApplication);
            if (innerApplication.arguments.every(
              (argument) => !usesVariables(argument, outerVariableIds)
            ) && innerFunctionExpression && arePermissionsMergable(functionExpression, innerApplication) && arePermissionsMergable(
              functionExpression,
              innerFunctionExpression
            )) {
              const newFunctionExpression = {
                ...functionExpression,
                valueType: {
                  type: "FunctionValueType",
                  parameterValueTypes: [
                    ...functionExpression.valueType.parameterValueTypes,
                    ...innerFunctionExpression.valueType.parameterValueTypes
                  ],
                  returnValueType: innerFunctionExpression.valueType.returnValueType
                },
                parameters: [
                  ...functionExpression.parameters,
                  ...innerFunctionExpression.parameters
                ],
                body: innerFunctionExpression.body
              };
              const newExpression = {
                ...expr,
                function: newFunctionExpression,
                arguments: [...expr.arguments, ...innerApplication.arguments]
              };
              return {
                newExpression,
                mapResult: 1
              };
            }
          }
        }
      }
      if (expr.type === "ComparisonExpression") {
        if (expr.a && expr.a.type === "ComparisonExpression" && !expr.b) {
          return {
            newExpression: expr.a,
            mapResult: 1
          };
        }
        if (expr.b && expr.b.type === "ComparisonExpression" && !expr.a) {
          return {
            newExpression: expr.b,
            mapResult: 1
          };
        }
        if (expr.a && expr.a.type === "VariableExpression" && expr.a.valueType.type === "BooleanValueType" && (expr.operator === "AND" || expr.operator === "OR")) {
          return {
            newExpression: {
              ...expr,
              a: {
                ...getComparisonExpression(),
                a: expr.a,
                operator: "==",
                b: getBooleanExpression(true)
              }
            },
            mapResult: 1
          };
        }
        if (expr.b && expr.b.type === "VariableExpression" && expr.b.valueType.type === "BooleanValueType" && (expr.operator === "AND" || expr.operator === "OR")) {
          return {
            newExpression: {
              ...expr,
              b: {
                ...getComparisonExpression(),
                a: expr.b,
                operator: "==",
                b: getBooleanExpression(true)
              }
            },
            mapResult: 1
          };
        }
        if (expr.a && (expr.a.type === "ComparisonExpression" || expr.a.type === "BooleanExpression") && expr.operator === "==" && expr.b && expr.b.type === "BooleanExpression" && expr.b.value) {
          return {
            newExpression: expr.a,
            mapResult: 1
          };
        }
        if (expr.a && expr.a.type === "SwitchExpression" && expr.a.cases.length === 1 && expr.a.cases[0].then && expr.a.cases[0].then.type === "BooleanExpression" && expr.a.cases[0].then.value && expr.a.default && expr.a.default.type === "BooleanExpression" && !expr.a.default.value) {
          return {
            newExpression: {
              ...expr,
              a: expr.a.cases[0].when
            },
            mapResult: 1
          };
        }
      }
      if (expr && expr.type === "ApplicationExpression") {
        if (expr.arguments.some(
          (arg) => arg && arg.type === "ComparisonExpression"
        )) {
          return {
            newExpression: {
              ...expr,
              arguments: expr.arguments.map(
                (arg) => arg && arg.type === "ComparisonExpression" ? {
                  ...getIfExpression(schema, {}, arg.valueType),
                  cases: [
                    {
                      when: arg,
                      then: getBooleanExpression(true)
                    }
                  ],
                  default: getBooleanExpression(false)
                } : arg
              )
            },
            mapResult: 1
          };
        }
      }
      if (expr && expr.type === "LogEventExpression") {
        if (expr.eventTypeId && !expr.eventObjectTypeName && eventTypeMap[expr.eventTypeId]) {
          const eventObjectTypeName = eventTypeMap[expr.eventTypeId].name;
          const eventPayload = expr.eventPayload || getDefaultExpression(
            schema,
            {},
            {
              type: "ObjectValueTypeConstraint",
              objectTypeName: eventObjectTypeName
            },
            /* @__PURE__ */ new Set()
          );
          if (!expr.eventPayload && Object.keys(schema.objects[eventObjectTypeName].fields).length === 1 && schema.objects[eventObjectTypeName].fields.unitId) {
            eventPayload.fields.unitId = copyExpression(expr.unitId);
          }
          return {
            newExpression: {
              ...expr,
              eventObjectTypeName,
              eventPayload
            },
            mapResult: 1
          };
        }
        if (expr.eventObjectTypeName && (!expr.eventTypeId || !eventTypeMap[expr.eventTypeId] || eventTypeMap[expr.eventTypeId].name !== expr.eventObjectTypeName)) {
          const eventTypeId = Object.values(eventTypeMap).find(
            (value) => value.name === expr.eventObjectTypeName
          )?.id;
          if (eventTypeId) {
            return {
              newExpression: {
                ...expr,
                eventTypeId
              },
              mapResult: 1
            };
          }
        }
      }
      if (expr && expr.type === "SplitExpression") {
        return fixAndSimplifySplitExpression(schema, splitMap, expr);
      }
      if (expr && expr.type === "EnumSwitchExpression") {
        return fixAndSimplifyEnumSwitchExpression(schema, expr);
      }
      if (expr && expr.type === "SwitchExpression") {
        return fixAndSimplifySwitchExpression(schema, expr);
      }
      return { newExpression: expr, mapResult: 0 };
    },
    (...results) => results.reduce((prev, curr) => prev + curr, 0),
    expression
  );
  return {
    newExpression: nullThrows(
      result.newExpression,
      "fixAndSimplifyOnce returned null expression"
    ),
    numChanges: result.mapResult
  };
}
function isRedundantFunctionExpression(expression) {
  if (expression.type !== "FunctionExpression") {
    return false;
  }
  const application = expression.body;
  if (!application || application.type !== "ApplicationExpression") {
    return false;
  }
  const functionVariable = application.function;
  if (!functionVariable || functionVariable.type !== "VariableExpression" || functionVariable.valueType.type !== "FunctionValueType") {
    return false;
  }
  if (expression.parameters.length !== application.arguments.length || application.arguments.length !== functionVariable.valueType.parameterValueTypes.length) {
    return false;
  }
  return expression.parameters.every((parameter, index) => {
    const argument = application.arguments[index];
    return argument && argument.type === "VariableExpression" && argument.variableId === parameter.id;
  });
}
function arePermissionsMergable(parent, child) {
  if (!parent.metadata?.permissions && !child.metadata?.permissions) {
    return true;
  }
  if (!parent.metadata?.permissions) {
    return false;
  }
  const childResolvedPermissions = resolvePermissions(
    parent.metadata.permissions,
    child.metadata?.permissions
  );
  return childResolvedPermissions === parent.metadata.permissions || stableStringify(childResolvedPermissions) === stableStringify(parent.metadata.permissions);
}
function combineNotes(...notes) {
  return notes.filter(Boolean).map((note) => {
    const trimmed = note.trimEnd();
    return trimmed.endsWith(".") ? trimmed : `${trimmed}.`;
  }).join(" ") || void 0;
}
function fixAndSimplifyEnumSwitchExpression(schema, currentExpression) {
  let newExpression = currentExpression;
  let mapResult = 0;
  const schemaEnumValues = newExpression.control && newExpression.control.valueType.type === "EnumValueType" ? schema.enums[newExpression.control.valueType.enumTypeName]?.values || {} : {};
  Object.entries(newExpression.cases).forEach(
    ([enumValue, valueExpression]) => {
      if (typeof schemaEnumValues[enumValue] === "undefined" && isEmptyExpression(valueExpression)) {
        const { [enumValue]: _, ...cases } = newExpression.cases;
        newExpression = { ...newExpression, cases };
        mapResult = 1;
      }
    }
  );
  const missingEnumValues = [];
  Object.keys(schemaEnumValues).forEach((schemaEnumValue) => {
    if (typeof newExpression.cases[schemaEnumValue] === "undefined") {
      missingEnumValues.push(schemaEnumValue);
    }
  });
  if (missingEnumValues.length > 0) {
    const cases = { ...newExpression.cases };
    missingEnumValues.forEach((enumValue) => {
      cases[enumValue] = getDefaultExpression(
        schema,
        {},
        getConstraintFromValueType(newExpression.valueType),
        /* @__PURE__ */ new Set()
      );
    });
    newExpression = { ...newExpression, cases };
    mapResult = 1;
  }
  return { newExpression, mapResult };
}
function fixAndSimplifySplitExpression(schema, splitMap, currentExpression) {
  let newExpression = currentExpression;
  let mapResult = 0;
  if (newExpression.splitId && splitMap[newExpression.splitId] && newExpression.eventObjectTypeName !== splitMap[newExpression.splitId].eventObjectTypeName) {
    newExpression = {
      ...newExpression,
      eventObjectTypeName: splitMap[newExpression.splitId].eventObjectTypeName,
      eventPayload: splitMap[newExpression.splitId].eventObjectTypeName ? newExpression.eventPayload || null : null
    };
    mapResult += 1;
  }
  if (newExpression.splitId && splitMap[newExpression.splitId]) {
    const split = splitMap[newExpression.splitId];
    const dimensions = Object.values(split.dimensions);
    if (!newExpression.dimensionId && dimensions.length === 1 && dimensions[0].type === "discrete") {
      newExpression = { ...newExpression, dimensionId: dimensions[0].id };
      mapResult += 1;
    }
    const dimension = newExpression.dimensionId && split && split.dimensions[newExpression.dimensionId] ? split.dimensions[newExpression.dimensionId] : null;
    const needDefaultArm = isDefaultArmNeeded(split, dimension);
    const extraArmIds = [];
    if (newExpression.dimensionMapping.type === "discrete") {
      Object.keys(newExpression.dimensionMapping.cases).forEach((armId) => {
        if (armId === defaultArmKey) {
          if (!needDefaultArm) {
            extraArmIds.push(armId);
          }
          return;
        }
        const arm = dimension && dimension.type === "discrete" && dimension.arms[armId] ? dimension.arms[armId] : null;
        if (!arm) {
          extraArmIds.push(armId);
        }
      });
    }
    const missingArmIds = [];
    if (dimension && dimension.type === "discrete") {
      Object.keys(dimension.arms).concat(needDefaultArm ? [defaultArmKey] : []).forEach((armId) => {
        armId === defaultArmKey ? Number.MAX_SAFE_INTEGER : dimension.arms[armId].index;
        if (newExpression.dimensionMapping.type !== "discrete" || typeof newExpression.dimensionMapping.cases[armId] === "undefined") {
          missingArmIds.push(armId);
        }
      });
    }
    if (dimension && dimension.type !== newExpression.dimensionMapping.type) {
      newExpression = {
        ...newExpression,
        dimensionMapping: dimension.type === "discrete" ? {
          type: "discrete",
          cases: {}
        } : {
          type: "continuous",
          function: null
        }
      };
      mapResult += 1;
    }
    if (newExpression.dimensionMapping.type === "discrete") {
      const newDimensionMapping = deepClonePlainObject(
        newExpression.dimensionMapping
      );
      let didUpdate = false;
      extraArmIds.forEach((armId) => {
        if (isEmptyExpression(newDimensionMapping.cases[armId])) {
          delete newDimensionMapping.cases[armId];
          didUpdate = true;
        }
      });
      const childValueTypeConstraint = isValueTypeValid(
        schema,
        newExpression.valueType
      ) ? getConstraintFromValueType(newExpression.valueType) : { type: "ErrorValueTypeConstraint" };
      if (missingArmIds.length > 0) {
        missingArmIds.forEach((armId) => {
          newDimensionMapping.cases[armId] = getDefaultExpression(
            schema,
            {},
            childValueTypeConstraint,
            /* @__PURE__ */ new Set(),
            {
              defaultBooleanValue: dimension?.type === "discrete" && armId !== dimension.controlArmId && armId !== defaultArmKey
            }
          );
        });
        didUpdate = true;
      }
      if (didUpdate) {
        newExpression = {
          ...newExpression,
          dimensionMapping: newDimensionMapping
        };
        mapResult += 1;
      }
    }
  }
  return { newExpression, mapResult };
}
function fixAndSimplifySwitchExpression(schema, expression) {
  if (isIfElseExpression(expression) && expression.cases.some(
    (c) => c.when && c.when.type !== "ComparisonExpression" && c.when.type !== "BooleanExpression"
  )) {
    return {
      newExpression: {
        ...expression,
        cases: expression.cases.map((c) => ({
          ...c,
          when: c.when && c.when.type !== "ComparisonExpression" ? {
            ...getComparisonExpression(),
            a: c.when,
            operator: "==",
            b: getBooleanExpression(true)
          } : c.when
        }))
      },
      mapResult: 1
    };
  }
  if (isIfElseExpression(expression) && expression.cases.length === 0 && expression.default) {
    return {
      newExpression: expression.default,
      mapResult: 1
    };
  }
  return { newExpression: expression, mapResult: 0 };
}

// src/expression/expressionsAreEqual.ts
function expressionsAreEqual(a, b) {
  if (a === null) {
    return b === null;
  }
  if (b === null) {
    return a === null;
  }
  if (a.type !== b.type || !valueTypesAreEqual(a.valueType, b.valueType)) {
    return false;
  }
  switch (a.type) {
    case "NoOpExpression":
      return true;
    case "BooleanExpression":
    case "StringExpression":
    case "IntExpression":
    case "FloatExpression":
    case "RegexExpression":
    case "EnumExpression":
      return a.value === b.value;
    case "VariableExpression":
      return a.variableId === b.variableId;
    case "ObjectExpression": {
      const bExpr = b;
      return a.objectTypeName === bExpr.objectTypeName && objectsOfExpressionsAreEqual(a.fields, bExpr.fields);
    }
    case "GetFieldExpression": {
      const bExpr = b;
      return a.fieldPath === bExpr.fieldPath && expressionsAreEqual(a.object, bExpr.object);
    }
    case "UpdateObjectExpression": {
      const bExpr = b;
      return objectsOfExpressionsAreEqual(a.updates, bExpr.updates) && expressionsAreEqual(a.object, bExpr.object);
    }
    case "ListExpression": {
      const bExpr = b;
      return a.items.length === bExpr.items.length && !a.items.some(
        (aItem, index) => !expressionsAreEqual(aItem, bExpr.items[index] ?? null)
      );
    }
    case "SwitchExpression": {
      const bExpr = b;
      return expressionsAreEqual(a.control, bExpr.control) && expressionsAreEqual(a.default, bExpr.default) && a.cases.length === bExpr.cases.length && !a.cases.some(
        ({ when: aWhen, then: aThen }, index) => !expressionsAreEqual(aWhen, bExpr.cases[index]?.when ?? null) || !expressionsAreEqual(aThen, bExpr.cases[index]?.then ?? null)
      );
    }
    case "EnumSwitchExpression": {
      const bExpr = b;
      return expressionsAreEqual(a.control, bExpr.control) && objectsOfExpressionsAreEqual(a.cases, bExpr.cases);
    }
    case "ArithmeticExpression":
    case "ComparisonExpression": {
      const bExpr = b;
      return a.operator === bExpr.operator && expressionsAreEqual(a.a, bExpr.a) && expressionsAreEqual(a.b, bExpr.b);
    }
    case "RoundNumberExpression":
    case "StringifyNumberExpression": {
      const bExpr = b;
      return expressionsAreEqual(a.number, bExpr.number);
    }
    case "StringConcatExpression": {
      const bExpr = b;
      return expressionsAreEqual(a.strings, bExpr.strings);
    }
    case "GetUrlQueryParameterExpression": {
      const bExpr = b;
      return expressionsAreEqual(a.queryParameterName, bExpr.queryParameterName) && expressionsAreEqual(a.url, bExpr.url);
    }
    case "SplitExpression": {
      const bExpr = b;
      return a.splitId === bExpr.splitId && a.dimensionId === bExpr.dimensionId && a.eventObjectTypeName === bExpr.eventObjectTypeName && a.dimensionMapping.type === bExpr.dimensionMapping.type && (a.dimensionMapping.type === "discrete" ? objectsOfExpressionsAreEqual(
        a.dimensionMapping.cases,
        bExpr.dimensionMapping.cases
      ) : expressionsAreEqual(
        a.dimensionMapping.function,
        bExpr.dimensionMapping.function
      )) && expressionsAreEqual(a.expose, bExpr.expose) && expressionsAreEqual(a.unitId, bExpr.unitId) && expressionsAreEqual(a.eventPayload, bExpr.eventPayload) && objectsOfExpressionsAreEqual(a.featuresMapping, bExpr.featuresMapping);
    }
    case "LogEventExpression": {
      const bExpr = b;
      return a.eventObjectTypeName === bExpr.eventObjectTypeName && a.eventTypeId === bExpr.eventTypeId && expressionsAreEqual(a.unitId, bExpr.unitId) && expressionsAreEqual(a.eventPayload, bExpr.eventPayload) && objectsOfExpressionsAreEqual(a.featuresMapping, bExpr.featuresMapping);
    }
    case "FunctionExpression": {
      const bExpr = b;
      return a.parameters.length === bExpr.parameters.length && !a.parameters.some(
        (aParam, index) => aParam.id !== bExpr.parameters[index].id || aParam.name !== bExpr.parameters[index].name
      ) && expressionsAreEqual(a.body, bExpr.body);
    }
    case "ApplicationExpression": {
      const bExpr = b;
      return a.arguments.length === bExpr.arguments.length && !a.arguments.some(
        (aArg, index) => !expressionsAreEqual(aArg, bExpr.arguments[index])
      ) && expressionsAreEqual(a.function, bExpr.function);
    }
    default: {
      const neverExpression = a;
      throw new Error(`unexpected expression: ${neverExpression}`);
    }
  }
}
function objectsOfExpressionsAreEqual(a, b) {
  return (
    // Fields in a match fields in b
    !Object.entries(a).some(
      ([fieldName, aField]) => !expressionsAreEqual(aField, b[fieldName] ?? null)
    ) && // No extra fields in b
    !Object.keys(b).some((fieldName) => !(fieldName in a))
  );
}
function getExpressionHash(expression) {
  return hash(stableStringify(sanitizeExpression(expression)));
}
function backfillExpressionLegacyLogFields(expression) {
  return mapExpression((expr) => {
    if (!expr) {
      return null;
    }
    const logs = mergeLogs(expr.logs);
    return {
      ...expr,
      logs: {
        // We don't populate new fields to avoid double logging for new SDKs.
        evaluations: logs?.evaluations ?? {},
        events: logs?.eventList?.reduce((acc, event) => {
          const key = stableStringify(event);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {}) ?? {},
        exposures: logs?.exposureList?.reduce((acc, event) => {
          const key = stableStringify(event);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {}) ?? {}
      }
    };
  }, expression);
}
function reconcileSchemaAndEventTypeMap(schema, eventTypeMap, options) {
  const schemaEvents = Object.fromEntries(
    Object.entries(schema.objects).filter(
      ([, objectSchema]) => objectSchema.role === "event"
    )
  );
  const eventTypeNames = Object.fromEntries(
    Object.entries(schema.objects).filter(([, objectSchema]) => objectSchema.role !== "event").map(([name]) => [name, true]).concat(Object.keys(schema.enums).map((name) => [name, true])).concat(Object.keys(schema.unions).map((name) => [name, true]))
  );
  const eventTypeMapByName = Object.fromEntries(
    Object.values(eventTypeMap).map((eventType) => {
      const name = getNextName(
        eventTypeNames,
        formatTypeSchemaName(eventType.name)
      );
      return [name, { ...eventType, name }];
    })
  );
  const newSchema = options?.discardExtraEventTypesInTypeMap ? schema : Object.values(eventTypeMapByName).reduce((currentSchema, eventType) => {
    if (eventType.name in schemaEvents) {
      return currentSchema;
    }
    return addFieldToObject(
      addEmptyObject(currentSchema, eventType.name, "event", null),
      eventType.name,
      "unitId",
      { type: "StringValueType" },
      /* description */
      null
    );
  }, schema);
  const startEventTypeMap = Object.fromEntries(
    Object.values(eventTypeMapByName).filter(
      (eventType) => !options?.discardExtraEventTypesInTypeMap || eventType.name in schemaEvents
    ).map((eventType) => [eventType.id, eventType])
  );
  const newEventTypeMap = Object.keys(schemaEvents).reduce(
    (currentEventTypes, eventName) => {
      if (eventName in eventTypeMapByName) {
        return currentEventTypes;
      }
      const newEventId = uniqueId$1();
      return {
        ...currentEventTypes,
        [newEventId]: { id: newEventId, name: eventName, featureIds: {} }
      };
    },
    startEventTypeMap
  );
  return { newSchema, newEventTypeMap };
}

// src/splitRewardSql.ts
function getSplitGoalEventTypeIdFromRewardSql(rewardSql) {
  const regex = /LEAST\(COUNT\(\*\) FILTER \(WHERE event_type_id = '([A-Za-z0-9_-]+)'\), 1\)/;
  const result = regex.exec(rewardSql);
  return result ? result[1] || null : null;
}
function getSplitGoalEventTypeNameFromRewardSql(rewardSql) {
  const regex = /LEAST\(COUNT\(\*\) FILTER \(WHERE event_object_type_name = '([A-Za-z0-9_-]+)'\), 1\)/;
  const result = regex.exec(rewardSql);
  return result ? result[1] || null : null;
}
function getSplitRewardSql(goalEventTypeName) {
  return `LEAST(COUNT(*) FILTER (WHERE event_object_type_name = '${goalEventTypeName}'), 1)`;
}

// src/reconcileSchemaAndImplementation.ts
function reconcileSchemaAndImplementation(schema, splits, eventTypeMap, expression) {
  const { newSchema, newEventTypeMap } = reconcileSchemaAndEventTypeMap(
    schema,
    eventTypeMap
  );
  const newSplits = reconcileSplits(newSchema, splits);
  const { newExpression } = fixAndSimplify(
    newSchema,
    newSplits,
    newEventTypeMap,
    null,
    expression
  );
  return {
    newSchema,
    newSchemaCode: getSchemaCodeFromSchema(newSchema),
    newEventTypeMap,
    newSplits,
    newExpression
  };
}
function reconcileSplits(schema, splits) {
  return Object.fromEntries(
    Object.entries(splits).map(([id, split]) => {
      let newSplit;
      if (split.type === "test") {
        newSplit = {
          type: "test",
          id: split.id,
          name: split.name,
          description: split.description,
          dimensions: split.dimensions,
          eventObjectTypeName: split.eventObjectTypeName ?? null,
          featureIds: {}
        };
      } else {
        const goalEventObjectTypeName = nullThrows(
          getSplitGoalEventTypeNameFromRewardSql(split.rewardSql),
          new Error(`missing eventObjectTypeName in split ${split.id}}`)
        );
        newSplit = {
          type: "ml",
          id: split.id,
          name: split.name,
          description: split.description,
          dimensions: split.dimensions,
          eventObjectTypeName: split.eventObjectTypeName ?? null,
          rewardSql: split.rewardSql,
          rewardEvents: split.rewardEvents || [
            {
              eventObjectTypeName: goalEventObjectTypeName,
              unitIdPayloadPath: getStringValuePathsForObject(
                schema,
                goalEventObjectTypeName
              )[0]
            }
          ],
          featureIds: {}
        };
      }
      return [id, newSplit];
    })
  );
}
function copyCommitData(commitData) {
  return {
    // Schema code is safe to copy directly.
    schemaCode: commitData.schemaCode,
    // The rest of the fields needs to have all ids replaced.
    ...copyImplementation(commitData)
  };
}
function copyImplementation(implementation, options) {
  const featureIdMap = {};
  const eventIdMap = {};
  const splitIdMap = {};
  const dimensionIdMap = {};
  const armIdMap = {};
  const expressionParamIdMap = {};
  const namePrefix = options?.namePrefix || "";
  const newEventTypes = Object.fromEntries(
    Object.entries(implementation.eventTypes).map(
      ([eventId, eventType]) => {
        const newEventId = uniqueId$1();
        eventIdMap[eventId] = newEventId;
        return [
          newEventId,
          {
            id: newEventId,
            name: namePrefix + eventType.name,
            featureIds: mapFeatureIds(featureIdMap, eventType.featureIds)
          }
        ];
      }
    )
  );
  const newSplits = Object.fromEntries(
    Object.values(implementation.splits).map((split) => {
      const newSplit = copySplit(split, namePrefix, dimensionIdMap, armIdMap);
      splitIdMap[split.id] = newSplit.id;
      return [newSplit.id, newSplit];
    })
  );
  const newExpression = mapExpression((expr) => {
    if (!expr) {
      return null;
    }
    const overrideFields = {
      id: uniqueId$1(),
      logs: {},
      metadata: expr.metadata ? {
        ...expr.metadata,
        permissions: isEmptyPermissions(expr.metadata.permissions) ? void 0 : {
          group: { [teamGroupId]: { write: "allow" } },
          user: {}
        }
      } : void 0
    };
    if (expr.type === "FunctionExpression") {
      return {
        ...expr,
        ...overrideFields,
        parameters: expr.parameters.map((param) => {
          const newParamId = options?.skipVariableCopy ? param.id : expressionParamIdMap[param.id] || uniqueId$1();
          expressionParamIdMap[param.id] = newParamId;
          return {
            ...param,
            id: newParamId
          };
        }),
        body: expr.body
      };
    }
    if (expr.type === "VariableExpression") {
      const newVariableId = options?.skipVariableCopy ? expr.variableId : expressionParamIdMap[expr.variableId] || uniqueId$1();
      expressionParamIdMap[expr.variableId] = newVariableId;
      return {
        ...expr,
        ...overrideFields,
        variableId: newVariableId
      };
    }
    if (expr.type === "LogEventExpression") {
      return {
        ...expr,
        ...overrideFields,
        eventTypeId: expr.eventTypeId ? eventIdMap[expr.eventTypeId] : null,
        featuresMapping: {}
      };
    }
    if (expr.type === "SplitExpression") {
      return {
        ...expr,
        ...overrideFields,
        dimensionId: expr.dimensionId ? dimensionIdMap[expr.dimensionId] : null,
        splitId: expr.splitId ? splitIdMap[expr.splitId] : null,
        dimensionMapping: expr.dimensionMapping.type === "discrete" ? {
          ...expr.dimensionMapping,
          cases: Object.fromEntries(
            Object.entries(expr.dimensionMapping.cases).map(
              ([armId, caseExpr]) => [armIdMap[armId], caseExpr]
            )
          )
        } : expr.dimensionMapping,
        featuresMapping: {}
      };
    }
    return {
      ...expr,
      ...overrideFields
    };
  }, implementation.expression);
  return {
    // The rest of the fields had all ids replaced.
    features: {},
    eventTypes: newEventTypes,
    splits: newSplits,
    expression: newExpression
  };
}
function copySplit(split, namePrefix, dimensionIdMap, armIdMap) {
  const name = namePrefix + split.name;
  const dimensions = Object.fromEntries(
    Object.entries(split.dimensions).map(
      ([dimensionId, dimension]) => {
        const newDimensionId = uniqueId$1();
        dimensionIdMap[dimensionId] = newDimensionId;
        return [
          newDimensionId,
          {
            ...dimension,
            id: newDimensionId,
            ...dimension.type === "discrete" ? {
              arms: Object.fromEntries(
                Object.entries(dimension.arms).map(
                  ([armId, arm]) => {
                    const newArmId = uniqueId$1();
                    armIdMap[armId] = newArmId;
                    return [
                      newArmId,
                      {
                        ...arm,
                        id: newArmId,
                        dimensionId: dimensionIdMap[arm.dimensionId]
                      }
                    ];
                  }
                )
              )
            } : {}
          }
        ];
      }
    )
  );
  if (split.type === "test") {
    return {
      id: uniqueId$1(),
      type: split.type,
      name,
      description: split.description,
      dimensions,
      eventObjectTypeName: split.eventObjectTypeName,
      featureIds: {}
    };
  }
  return {
    id: uniqueId$1(),
    type: split.type,
    name,
    description: split.description,
    dimensions,
    eventObjectTypeName: split.eventObjectTypeName,
    rewardEvents: split.rewardEvents,
    rewardSql: split.rewardSql,
    featureIds: {}
  };
}
function mapFeatureIds(featureIDMap, featureIds) {
  return Object.fromEntries(
    Object.keys(featureIds).map((featureId) => [
      featureIDMap[featureId],
      true
    ])
  );
}

// src/projectMetadata.ts
function getUpdateProjectMetadataUrlPath(token) {
  return getUpdateProjectMetadataUrlPathRaw(encodeURIComponent(token));
}
function getUpdateProjectMetadataUrlPathRaw(uriEncodedToken) {
  return `/project-metadata/${uriEncodedToken}`;
}
var updateCommitSyncStatusPath = "/update-commit-sync";

// src/errorHelpers.ts
function throwIfAnyError(results) {
  results.forEach((i) => {
    if (i instanceof Error) {
      throw i;
    }
  });
  return results;
}
function throwIfAnyPromiseRejected(results) {
  results.forEach((i) => {
    if (i.status === "rejected") {
      throw i.reason;
    }
  });
}

// src/trimQueryCode.ts
function trimQueryCode(queryCode) {
  return queryCode.trim().split("\n").map((line) => line.includes("#") ? line.split("#")[0] : line).map((line) => line.trim()).filter(Boolean).join(" ").split(/\s*{\s*/).join("{").split(/\s*}\s*/).join("}").split(/\s*\(\s*/).join("(").split(/\s*\)\s*/).join(")");
}

// src/sliceIntoChunks.ts
function sliceIntoChunks(array, chunkSize) {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    result.push(chunk);
  }
  return result;
}
function toInt(string) {
  const int = parseInt(nullThrows2(string, "[toInt] null string"), 10);
  if (Number.isNaN(int)) {
    throw new Error("[toInt] NaN");
  }
  return int;
}
function toFloat(string) {
  const int = parseFloat(nullThrows2(string, "[toInt] null string"));
  if (Number.isNaN(int)) {
    throw new Error("[toInt] NaN");
  }
  return int;
}

// src/unwrapValueType.ts
function unwrapValueType(valueType) {
  switch (valueType.type) {
    case "ListValueType":
      return unwrapValueType(valueType.itemValueType);
    case "FunctionValueType":
      return unwrapValueType(valueType.returnValueType);
    default:
      return valueType;
  }
}
function Button({
  children
}) {
  return /* @__PURE__ */ jsx("button", { type: "button", children });
}

export { Button, addDefaultEvent, addEmptyEnum, addEmptyObject, addEnumValue, addFieldToObject, allGraphqlScalarTypeNames, analyticsAggregationTypes, analyticsViewConfigFunnelType, analyticsViewConfigTableType, analyticsViewConfigTypes, anonymousIdCookieName, appVersionHeaderName, argsObjectSuffix, backendGraphqlEndpoint, backfillExpressionLegacyLogFields, canWrite, changeObjectFieldType, changeObjectRole, checkSchemaName, cloneObject, collectSplitAndEventTypeIds, compareObjectRolesAndNames, contextInputFieldsSet, contextInputFieldsWeights, contextInputsSet, contextInputsWeights, cookieConsentCookieName, copyCommitData, copyExpression, copyImplementation, deepClonePlainObject, deepEqualsPlainObject, defaultArmLabel, deleteSplitDiscreteDimensionArm, editableSchemaCodeFilter, enumToCode, expressionsAreEqual, filterAndSortObjectEntries, fixAndSimplify, formatEnumValueSchemaName, formatFieldSchemaName, formatSchemaCode, formatTypeSchemaName, funnelEventStepType, funnelExposureStepType, funnelStepTypes, getAllValuePathsForObject, getDefaultQueryCode, getEmptyPermissions, getEpsilonGreedySplitConfig, getExpressionHash, getFieldArgumentsObjectTypeName, getFieldArgumentsObjectTypeNameParts, getFullSchemaCode, getGraphqlSchema, getInitDataResponse, getNextName, getNextSchemaName, getObjectTypeSchemaReferences as getOutputObjectReferences, getQueryCodeFromQuery, getSchema, getSchemaAndQuery, getSchemaCodeFromSchema, getSchemaWithDescriptions, getSplitGoalEventTypeIdFromRewardSql, getSplitGoalEventTypeNameFromRewardSql, getSplitRewardSql, getStringValuePathsForObject, getUpdateProjectMetadataUrlPath, getUpdateProjectMetadataUrlPathRaw, getVercelEdgeConfigItemKey, getWebEnvironment, graphqlScalarTypeNames, hashRequestLogSamplingRate, isAllowed, isEmptyPermissions, isFieldArgumentsObjectVariableExpression, isPrimitiveValueType, isSchemaNameValid, isValidDomain, isValidEmail, isValidGraphqlInputType, isValidUrl, kilSwitchExpressionNote, makeGraphqlSchemaInputTypesOptional, newSchemaRootComment, newSchemaWelcomeMessage, objectFieldToCode, objectToCode, oldSchemaRootComment, oldSchemaWelcomeMessage, otherExpressionOptionsGroupLabel, plansPath, projectTokenMapSchema, projectTokenScopesArray, queryObjectTypeName, queryRootArgsObjectTypeName, queryRootArgsTypeName, reconcileSchemaAndEventTypeMap, reconcileSchemaAndImplementation, removeEnum, removeEnumValue, removeFieldFromObject, removeFieldsFromObjects, removeObject, removeObjectFieldsFromExpression, removeSplitArmFromExpression, renameEnum, renameEnumInExpression, renameEnumValue, renameEnumValueInExpression, renameObject, renameObjectField, renameObjectFieldInExpression, renameObjectInExpression, replaceVariablePlaceholders, reservedTypeNames, resolvePermissions, rightDoubleArrowSymbol, rootContextTypeNameFromSchema, rootFieldArgsTypeName, rootFieldName, rootObjectTypeName, rootObjectTypeNameFromSchema, sanitizeExpression, sdkVersions, sessionIdHeaderName, sessionIdParamName, setEnumDescription, setEnumValueDeprecationReason, setEnumValueDescription, setObjectDescription, setObjectFieldDeprecationReason, setObjectFieldDescription, sliceIntoChunks, sourceObjectTypeName, splitSchemaCode, stringifyWithoutQuotes, teamGroupId, throwIfAnyError, throwIfAnyPromiseRejected, toCamelCase, toFloat, toInt, trimQueryCode, unimplementedExpressionErrorMessage, unwrapValueType, updateActiveCommitRequestBody, updateCommitSyncBodySchema, updateCommitSyncStatusPath, valueTypeToString, variableExpressionOptionsGroupLabel, variablePathSuffix, vercelFlagPathSeparator, voidGraphqlScalarTypeName };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map